'use client'

import { Avatar, Input, Modal, Switch, Tag, Tooltip, message } from 'antd'
import clsx from 'clsx'
import { useSearchParams, usePathname } from 'next/navigation'
import styles from './Create.module.css'
import Image from 'next/image'
import Link from 'next/link'
import {
  CreateCharacterTagsNSFW,
  CreateCharacterTagsSFW,
  TagLookupNSFW,
  TagLookupSFW,
  filterTagsForUpdatedCharacter,
} from '@/constant/tags'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { IdList, ProfileCharacterList, SelfStory } from '@/types/character'
import {
  MAX_DIALOG_STYLE_LENGTH,
  MAX_GREETING_LENGTH,
  MAX_LONG_DESCRIBE_LENGTH,
  MAX_NAME_LENGTH,
  MAX_OPTIMIZATION_CODE_LENGTH,
  MAX_REPLY_REQUIREMENT_LENGTH,
  MAX_SCENARIO_LENGTH,
  MAX_SHORT_DESCRIBE_LENGTH,
  MIN_GREETING_LENGTH,
  UPLOAD_IMG_CROP_RATIO,
} from '@/constant/limit'
import TipsyButton from 'Comps/ui-kit/TipsyButton'
import { CloseOutlinedIcon } from '@/components/svg'
import { ProfileEditCharIcon } from '@/components/svg/ProfileEditCharIcon'
import {
  CreatePromptIcon,
  CreateRulesIcon,
  TagEventIcon,
  TagNewIcon,
} from 'Icons'
import ProfileLockIcon from '@/components/svg/ProfileLockIcon'

import useCreateDetails from 'Hooks/create/useCreateDetails'
import useCreateImg from 'Hooks/create/useCreateImg'
import './creat.css'
import { transformImage } from 'Utils/img'
import useI18n from 'Hooks/useI18n'
import useCreateConversation from 'Hooks/create/useCreateConversation'
import PumpkinLampIcon from '@/icons/Halloween2025/pumpkinLampIcon'
import {
  Halloween2025tag,
  Christmas2025tag,
  Valentines2026tag,
} from '@/components/svg'
import { ANNIVERSARY_DEADLINE_TIME } from '@/constant/date'
import useCountdown from '@/hooks/useCountdown'
import PreviewDom from '@/components/create-preview/PreviewDom'
import { getImagePrimaryColor } from '@/apis/create'
import { getCharacterPublicByUser } from '@/apis/character'
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil'
import { createStateFactory } from '@/store/create'
import { userState } from '@/store/user'
import { withLocale } from '@/locales'
import { GuideRegistryKey } from '@/guide/guideRegistry'
import { useGuideStatusItem } from '@/store/guideStatus'
import {
  ChatInspirationZoomLightIcon,
  ChatInspirationCollapseLightIcon,
} from '@/components/svg/enhanced'
import TestChatLocalTooltip from './_components/TestChatLocalTooltip'
import TestChatRefreshIcon from '@/components/svg/TestChatRefreshIcon'
import TestChatTipCloseIcon from '@/components/svg/TestChatTipCloseIcon'
import useTestChatStream from '@/hooks/chat/useTestChatStream'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import ImageToLinkModal, {
  ImageLinkRecord,
} from './_components/ImageToLinkModal'
import { getUploadedCodingImg } from '@/apis/create'
import CreationGuideModal from './_components/CreationGuideModal'
import OpeningGuideModal from './_components/OpeningGuideModal'
import LayoutGuideModal from './_components/LayoutGuideModal'
import OutputGuideModal from './_components/OutputGuideModal'
import { getOfficialExampleHtml } from './_components/officialExampleHtml'
import { getOfficialExampleOpeningHtml } from './_components/officialExampleOpening'
import { getOfficialExampleLayoutHtml } from './_components/officialExampleLayout'
import { getOfficialExampleOutputText } from './_components/officialExampleOutput'
import TagDisplayLabel from '@/components/common/TagDisplayLabel'
import { resolveTagDisplaysByIds } from '@/utils/tagDisplay'
import {
  isRichContentLike,
  richContentToPlainText,
} from '@/utils/richContentPlainText'
import {
  ExclamationCircleOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons'
import useProfileCharacters from '@/hooks/profile/useProfileCharacters'
import { ReviewStage } from '@/types/review'
import { ISearchResultData } from '@/types/serach'
import StoryCharacterDrawer from './_components/StoryCharacterDrawer'
import { getPlatform } from '@/utils/platform'
import { BackgroundFileUploader } from '@/components-mobile/create/BackgroundFileUploader'
import GuideActionBar, { GuideActionButton } from './_components/GuideActionBar'
import CreateImg from './CreateImg'
import AvatarEditorItem, {
  AvatarEditorItemOutput,
} from '@/app/[lang]/@desktop/_components/AvatarEditorItem'
import useVoices from '@/hooks/useVoices'
import { GenderFilterOption } from '@/components-mobile/common/GenderFilterButtons'
import VoiceList, { SelectedVoiceItem } from './_components/VoiceList'
import { VoiceItem } from '@/apis/voice'
import { AudioProvider } from '@/components/common/VoicePlayButton'
import CharacterDraftModal from '@/components/common/CharacterDraftModal'
import useCharacterDraftBox from '@/hooks/create/useCharacterDraftBox'
import RestoreLocalDraftModal from '@/components/common/RestoreLocalDraftModal'
import useLocalCharacterDraftCache from '@/hooks/create/useLocalCharacterDraftCache'
import SearchIcon from 'Comps/svg/SearchIcon'
import { CreateIconGuide } from '@/components/svg'
import { useStoryStore } from '@/store/story'
import GreetingVideoUploader from '@/components/create/GreetingVideoUploader'
import CharacterCreationConfirmation from '@/components/common/CharacterCreationConfirmation'
import useCharacterCreationConfirmation from '@/hooks/create/useCharacterCreationConfirmation'
import {
  createCharacterAnalytics,
  getCharacterCreateSession,
  startCharacterCreateSession,
} from '@/lib/analytics/characterCreateAnalytics'

type CreateDetailsProps = {
  setStep?: Dispatch<SetStateAction<number>>
}

type ImageEntryType = 'upload' | 'generate' | 'gif'

// Web 暂无问候视频消费链路，暂不展示 Intro Video 上传配置。
const ENABLE_WEB_GREETING_VIDEO_CONFIG = false

const normalizeSearchText = (value: string) => value.trim().toLowerCase()

const buildSelectedIdList = (
  selectedIds: string[],
  characters: ProfileCharacterList[],
  fallbackList: IdList[]
) => {
  const fallbackMap = new Map(
    fallbackList.map((item) => [item.character_id, item] as const)
  )
  const characterMap = new Map(
    characters.map((item) => [item.character_id, item] as const)
  )

  return selectedIds
    .map((id) => {
      const fallback = fallbackMap.get(id)
      if (fallback) {
        return fallback
      }

      const matched = characterMap.get(id)
      if (!matched) {
        return null
      }

      return {
        character_id: matched.character_id,
        nickname: matched.nickname,
        image_url: matched.image_url,
      }
    })
    .filter((item): item is IdList => !!item)
}

export default function CreateDetails({ setStep }: CreateDetailsProps) {
  const { t, lang } = useI18n()
  const { trackEvent } = useQuickTracking()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const preselectTag = searchParams.get('preselect_tag')
  const isUpdate = searchParams.get('update') === 'true'
  const from = searchParams.get('from')
  const isEdit = searchParams.get('isEdit') === 'true'
  const isUpdateChar =
    pathname === withLocale('/update', lang) ||
    searchParams.get('update') === 'true'
  const charFormState = useMemo(
    () => createStateFactory(isUpdateChar, from || 'character_image'),
    [from, isUpdateChar]
  )
  const storyFormState = useStoryStore(isEdit)
  const storyUserImageFormState = useMemo(
    () => createStateFactory(isEdit, 'story_user_image'),
    [isEdit]
  )
  const [charForm, setCharForm] = useRecoilState(charFormState)
  const resetCharForm = useResetRecoilState(charFormState)
  const resetStoryForm = useResetRecoilState(storyFormState)
  const resetStoryUserImageForm = useResetRecoilState(storyUserImageFormState)
  const user = useRecoilValue(userState)
  const characterId = searchParams.get('character_id')
  const [primaryColor, setPrimaryColor] = useState<string>('#9C4844')
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [openingExpanded, setOpeningExpanded] = useState(false)
  const [backgroundExpanded, setBackgroundExpanded] = useState(false)
  const [replySettingExpanded, setReplySettingExpanded] = useState(false)
  const [layoutCustomizeExpanded, setLayoutCustomizeExpanded] = useState(false)
  const [outputSettingExpanded, setOutputSettingExpanded] = useState(false)
  const [imageToLinkOpen, setImageToLinkOpen] = useState(false)
  const [records, setRecords] = useState<ImageLinkRecord[]>([])
  const [hasViewedImageToLink, setHasViewedImageToLink] =
    useGuideStatusItem<boolean>(GuideRegistryKey.CREATE_IMAGE_TO_LINK_VIEWED)
  const platform = getPlatform()
  const characterAnalytics = useMemo(
    () =>
      createCharacterAnalytics({
        userId: user?.user_id,
        platform: 'web_pc',
        trackEvent,
      }),
    [trackEvent, user?.user_id]
  )
  const exposedCreateSessionIdRef = useRef('')
  const trackCreatePageExposure = useCallback(() => {
    const sessionId = getCharacterCreateSession()?.createSessionId
    if (!sessionId || exposedCreateSessionIdRef.current === sessionId) return

    const result = characterAnalytics.pageExposure()
    if (
      result.status === 'sent' ||
      (result.status === 'suppressed' && result.reason === 'duplicate_event')
    ) {
      exposedCreateSessionIdRef.current = sessionId
    }
  }, [characterAnalytics])
  const [creationGuideOpen, setCreationGuideOpen] = useState(false)
  const [openingGuideOpen, setOpeningGuideOpen] = useState(false)
  const [layoutGuideOpen, setLayoutGuideOpen] = useState(false)
  const [outputGuideOpen, setOutputGuideOpen] = useState(false)
  const [tipsyTagRefOpen, setTipsyTagRefOpen] = useState(false)
  const [imageCreatorOpen, setImageCreatorOpen] = useState(false)
  const [avatarEditorOpen, setAvatarEditorOpen] = useState(false)
  const [voiceModalOpen, setVoiceModalOpen] = useState(false)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)
  const {
    isCreationConfirmed,
    showCreationConfirmationError,
    setCreationConfirmed,
    validateCreationConfirmation,
    resetCreationConfirmation,
  } = useCharacterCreationConfirmation()

  const {
    charImageUrl,
    charFaceImagePreviewUrl,
    charNickname,
    charGender,
    character_type,
    role_character_ids,
    role_character_list,
    plot_file_key,
    plot_file_name,
    plot_file_prefix,
    charShortDescribe,
    styledTagline,
    charLongDescribe,
    showCharLongDescribe,
    replyRequirement,
    canShowReplySetting,
    contentType,
    charTagIDs,
    charScenario,
    charGreeting,
    styledGreeting,
    advancedSetCheck,
    conversationStyleData,
    conversationStyleKey,
    charDialogStyleFirstAllRoleMessage,
    nsfwCheck,
    setNsfwCheck,
    setCharNickname,
    setCharGender,
    setCharacterType,
    setChildCharacterIds,
    setRoleCharacterList,
    setPlotFileKey,
    setPlotFileName,
    setPlotFilePrefix,
    setUserRoleName,
    setUserRoleProfile,
    setUserRoleImage,
    setUserRoleImageUrl,
    setUserRoleFaceUrl,
    setCharShortDescribe,
    setStyledTagline,
    setCharLongDescribe,
    setShowCharLongDescribe,
    setContentType,
    setAdvancedSetCheck,
    setCharScenario,
    setConversationStyleKey,
    setCharGreeting,
    setStyledGreeting,
    handleClickTag,
    goPrevStep,
    goNextStep,
    nextButtonDisabled,
    handleDialogInputChange,
    isCharNameError,
    isCharShortDescribeError,
    isCharLongDescribeError,
    isReplyRequirementError,
    isScenarioError,
    isCharGreetingError,
    isCharDialogStyleError,
    optimizationCode,
    outputFormatConfig,
    setReplyRequirement,
    setOptimizationCode,
    setOutputFormatConfig,
    setIsPlotFileUploading,
    world_books,
    addWorldBook,
    removeWorldBook,
    setIsWorldBookUploading,
  } = useCreateDetails({ setStep, enableCustomPrompt: true })
  const uploadGifReferencePreviewUrl = charForm.uploadGifReferencePreviewUrl
  const uploadGifUrl = charForm.uploadGifUrl
  const greetingVideoKey = charForm.greetingVideoKey
  const greetingVideoUrl = charForm.greetingVideoUrl
  const greetingVideoUploading = charForm.greetingVideoUploading
  // 提交守卫：世界书/剧本文件上传在途时禁止提交，避免在传的 key 未入表就被提交丢弃
  // （与多步流程的 nextButtonDisabled 口径一致；单/多角色通用）。
  const isWorldBookUploading = charForm.is_world_book_uploading
  const isPlotFileUploading = charForm.is_plot_file_uploading
  const { croppingCharFaceImage, handleSaveFaceImg } = useCreateImg({
    setEditFaceOpen: setAvatarEditorOpen,
    scene: from || 'character_image',
  })
  const charFaceImageCroppedArea = charForm.charFaceImageCroppedArea
  const charFaceImageCroppedAreaPixels = charForm.charFaceImageCroppedAreaPixels

  const tagOptions = nsfwCheck
    ? CreateCharacterTagsNSFW
    : CreateCharacterTagsSFW
  const visibleTagOptions = useMemo(
    () =>
      isUpdateChar
        ? filterTagsForUpdatedCharacter(
            tagOptions,
            charForm.charCreatedAt,
            charTagIDs
          )
        : tagOptions,
    [charForm.charCreatedAt, charTagIDs, isUpdateChar, tagOptions]
  )
  const selectedTagOptions = nsfwCheck ? TagLookupNSFW : TagLookupSFW
  const selectedTagItems = resolveTagDisplaysByIds(
    charTagIDs,
    selectedTagOptions
  )
  const showFormatField =
    isUpdateChar && character_type === 1 && contentType === 2
  const showHtmlStylingSwitch = character_type === 1 && contentType !== 2
  const isLegacyHtml = contentType === 2
  const isStyledMarkdown = contentType === 3
  const isRichDisplayContent = isLegacyHtml || isStyledMarkdown
  const displayDescription = isRichDisplayContent
    ? styledTagline
    : charShortDescribe
  const displayOpening = isRichDisplayContent ? styledGreeting : charGreeting
  const plainDescriptionForSubmit = isStyledMarkdown
    ? charShortDescribe || richContentToPlainText(styledTagline)
    : charShortDescribe
  const plainOpeningForSubmit = isStyledMarkdown
    ? charGreeting || richContentToPlainText(styledGreeting)
    : charGreeting
  const isDisplayDescriptionError =
    displayDescription.length > MAX_SHORT_DESCRIBE_LENGTH
  const isDisplayOpeningError = displayOpening.length > MAX_GREETING_LENGTH

  const {
    charIsPublic,
    charBaseTone: charBaseToneString,
    createdLoading,
    voiceParam,
    setCharIsPublic,
    handleClickBaseTone,
    handleRemoveVoice,
    handleSpeedChange,
    handleCreate,
  } = useCreateConversation({
    setStep,
    enableCustomPrompt: true,
    validateCreationConfirmation,
  })
  useEffect(() => {
    trackCreatePageExposure()
  }, [trackCreatePageExposure])
  const {
    draftModalOpen,
    setDraftModalOpen,
    draftLoading,
    draftDeletingId,
    draftItems,
    draftTotal,
    draftSaving,
    saveCurrentDraft,
    loadDraftById,
    deleteDraftById,
  } = useCharacterDraftBox({
    charForm: greetingVideoUploading
      ? {
          ...charForm,
          greetingVideoKey: undefined,
          greetingVideoUrl: undefined,
        }
      : charForm,
    setCharForm,
    isUpdateChar,
    t,
  })
  const {
    restoreModalOpen,
    restoreCache,
    discardCache,
    clearCache: clearLocalDraftCache,
  } = useLocalCharacterDraftCache({
    charForm: greetingVideoUploading
      ? {
          ...charForm,
          greetingVideoKey: undefined,
          greetingVideoUrl: undefined,
        }
      : charForm,
    setCharForm,
    userId: user?.user_id,
    isUpdateChar,
    source: from || 'character_image',
    characterId,
    cacheEnabled: !isUpdateChar,
  })
  const handleSaveCurrentDraft = useCallback(async () => {
    characterAnalytics.saveClick()
    if (!isUpdateChar && !validateCreationConfirmation()) return

    const saved = await saveCurrentDraft()
    if (saved) {
      clearLocalDraftCache()
    }
  }, [
    clearLocalDraftCache,
    characterAnalytics,
    isUpdateChar,
    saveCurrentDraft,
    validateCreationConfirmation,
  ])
  const handleLoadDraftById = useCallback(
    async (draftId: string) => {
      if (!isUpdateChar) {
        resetCreationConfirmation()
      }
      const loaded = await loadDraftById(draftId)
      if (!loaded) return

      startCharacterCreateSession({
        triggerSource: 'draft_box',
        operationType: isUpdateChar ? 'update' : 'create',
        forceNewSession: true,
      })
      trackCreatePageExposure()
    },
    [
      isUpdateChar,
      loadDraftById,
      resetCreationConfirmation,
      trackCreatePageExposure,
    ]
  )
  const handleRestoreCache = useCallback(async () => {
    resetCreationConfirmation()
    await restoreCache()
  }, [resetCreationConfirmation, restoreCache])
  const { uniqueCharacterData } = useProfileCharacters(true)
  const [characterDrawerOpen, setCharacterDrawerOpen] = useState(false)
  const [drawerIdList, setDrawerIdList] = useState<IdList[]>([])
  const [searchText, setSearchText] = useState('')
  const [userDrawerOpen, setUserDrawerOpen] = useState(false)
  const [userDrawerTab, setUserDrawerTab] = useState<'profile' | 'avatar'>(
    'profile'
  )
  const [hasUserAvatar, setHasUserAvatar] = useState(false)
  const [voiceSearchTerm, setVoiceSearchTerm] = useState('')
  const [voiceSearchInput, setVoiceSearchInput] = useState('')
  const voiceSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const [voiceGenderFilter, setVoiceGenderFilter] = useState<
    'male' | 'female' | undefined
  >(
    charGender === 'male'
      ? 'male'
      : charGender === 'female'
        ? 'female'
        : undefined
  )

  const storyUserForm = useRecoilValue(
    createStateFactory(isEdit, 'story_user_image')
  )
  const plotUploaderRef = useRef<HTMLDivElement>(null)

  const publicCharacterData = useMemo(() => {
    return (uniqueCharacterData as SelfStory[])
      .filter((item) => item.item_type === 'character')
      .map((item) => item.character)
      .filter(
        (item): item is ProfileCharacterList =>
          !!item && item.character_type === 1
      )
  }, [uniqueCharacterData])

  const selectedRoleList = useMemo(
    () =>
      buildSelectedIdList(
        role_character_ids || [],
        publicCharacterData,
        role_character_list || []
      ),
    [publicCharacterData, role_character_ids, role_character_list]
  )

  const characterStatusById = useMemo(() => {
    const statusMap = new Map<
      string,
      { is_public?: boolean; review_stage?: ReviewStage }
    >()
    ;(uniqueCharacterData as SelfStory[]).forEach((item) => {
      if (item.item_type !== 'character' || !item.character) return
      statusMap.set(item.character.character_id, {
        is_public: item.character.is_public,
        review_stage: item.character.review_stage,
      })
    })
    return statusMap
  }, [uniqueCharacterData])

  const hasProfileLock = useMemo(() => {
    if (character_type !== 2) return false
    if (charIsPublic !== 'true') return false
    return selectedRoleList.some((item) => {
      const status = characterStatusById.get(item.character_id)
      return (
        status?.is_public !== true || status?.review_stage !== ReviewStage.Pass
      )
    })
  }, [character_type, charIsPublic, characterStatusById, selectedRoleList])

  const searchResults = useMemo(() => {
    const keyword = normalizeSearchText(searchText)
    if (!keyword) return []
    return publicCharacterData
      .filter((item) => {
        const nickname = normalizeSearchText(item.nickname || '')
        return nickname.includes(keyword)
      })
      .sort((a, b) => {
        const aName = normalizeSearchText(a.nickname || '')
        const bName = normalizeSearchText(b.nickname || '')
        const aIndex = aName.indexOf(keyword)
        const bIndex = bName.indexOf(keyword)

        if (aIndex !== bIndex) {
          return aIndex - bIndex
        }

        return aName.localeCompare(bName)
      })
  }, [publicCharacterData, searchText])

  const hasNsfwCharacter = useMemo(() => {
    if (character_type !== 2) return false
    return selectedRoleList.some((selected) => {
      const char = publicCharacterData.find(
        (c) => c.character_id === selected.character_id
      )
      return char?.nsfw === true
    })
  }, [character_type, publicCharacterData, selectedRoleList])

  useEffect(() => {
    if (hasNsfwCharacter) {
      setNsfwCheck(true)
    }
  }, [hasNsfwCharacter, setNsfwCheck])

  useEffect(() => {
    setVoiceGenderFilter(
      charGender === 'male'
        ? 'male'
        : charGender === 'female'
          ? 'female'
          : undefined
    )
  }, [charGender])

  const {
    voices,
    isLoading: isVoiceLoading,
    isLoadingMore: isVoiceLoadingMore,
    hasMore: hasMoreVoices,
    loadMore: loadMoreVoices,
  } = useVoices({
    gender: voiceGenderFilter,
    search_term: voiceSearchTerm,
    size: character_type === 1 && voiceModalOpen ? 20 : 0,
  })

  const handleSearchVoice = useCallback(
    (text: string, gender: GenderFilterOption) => {
      setVoiceSearchTerm(text)
      setVoiceGenderFilter(
        gender === 'Male' ? 'male' : gender === 'Female' ? 'female' : undefined
      )
    },
    []
  )

  const handleVoiceSearchInputChange = useCallback((value: string) => {
    setVoiceSearchInput(value)

    if (voiceSearchDebounceRef.current) {
      clearTimeout(voiceSearchDebounceRef.current)
    }

    voiceSearchDebounceRef.current = setTimeout(() => {
      setVoiceSearchTerm(value)
    }, 300)
  }, [])

  const handleVoiceSearchClear = useCallback(() => {
    if (voiceSearchDebounceRef.current) {
      clearTimeout(voiceSearchDebounceRef.current)
      voiceSearchDebounceRef.current = null
    }

    setVoiceSearchInput('')
    setVoiceSearchTerm('')
  }, [])

  useEffect(() => {
    return () => {
      if (voiceSearchDebounceRef.current) {
        clearTimeout(voiceSearchDebounceRef.current)
      }
    }
  }, [])

  const handleSelectVoice = useCallback(
    (voice: VoiceItem) => {
      handleClickBaseTone(voice.voice_id)
    },
    [handleClickBaseTone]
  )

  const voiceFilterOptions = useMemo<
    { label: string; value: GenderFilterOption }[]
  >(
    () => [
      { label: t('All'), value: 'All' },
      { label: t('Male'), value: 'Male' },
      { label: t('Female'), value: 'Female' },
    ],
    [t]
  )
  const selectedVoiceFilter = useMemo<GenderFilterOption>(() => {
    if (voiceGenderFilter === 'male') return 'Male'
    if (voiceGenderFilter === 'female') return 'Female'
    return 'All'
  }, [voiceGenderFilter])

  const handleVoiceFilterChange = useCallback(
    (gender: GenderFilterOption) => {
      handleSearchVoice(voiceSearchInput, gender)
    },
    [handleSearchVoice, voiceSearchInput]
  )

  const { voices: selectedVoices } = useVoices({
    ...(charBaseToneString ? { voice_id: charBaseToneString } : {}),
    size: charBaseToneString && character_type === 1 ? 1 : 0,
    page: 0,
  })

  const selectedVoice = useMemo(() => {
    if (!charBaseToneString) return null
    if (selectedVoices && selectedVoices.length > 0) {
      return selectedVoices[0]
    }
    return voices.find((voice) => voice.voice_id === charBaseToneString) || null
  }, [charBaseToneString, selectedVoices, voices])

  const searchTotal = searchResults.length
  const searchLoading = false

  useEffect(() => {
    const element = plotUploaderRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          trackEvent('plot_uploader_view', { platform }, 'EXP')
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [platform, trackEvent])

  const validateBeforeTestChatSend = useCallback(() => {
    if (contentType !== 2) {
      message.error(t('Please complete all required fields.'))
      return false
    }
    if (
      !charNickname.trim() ||
      !charShortDescribe.trim() ||
      !charGreeting.trim() ||
      !charGender
    ) {
      message.error(t('Please complete all required fields.'))
      return false
    }
    return true
  }, [
    charGender,
    charGreeting,
    charNickname,
    charShortDescribe,
    contentType,
    t,
  ])

  const validateBeforeSinglePageSubmit = useCallback(() => {
    if (charNickname === '') {
      message.error({
        content: t('Name is required'),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (charGender === null) {
      message.error({
        content: t('Gender is required'),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (plainDescriptionForSubmit === '') {
      message.error({
        content: t('Short Description is required'),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (plainOpeningForSubmit === '') {
      message.error({
        content: t('Opening is required'),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (greetingVideoUploading) {
      message.error({
        content: t('Please wait for the video upload to finish.'),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (charIsPublic === '') {
      message.error({
        content: t('Visibility is required'),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (charNickname.length > MAX_NAME_LENGTH) {
      message.error({
        content: t('Name should be within {{expr}} characters', {
          expr: MAX_NAME_LENGTH,
        }),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (charLongDescribe.length > MAX_LONG_DESCRIBE_LENGTH) {
      message.error({
        content: t('Long Description should be within {{expr}} characters', {
          expr: MAX_LONG_DESCRIBE_LENGTH,
        }),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (displayDescription.length > MAX_SHORT_DESCRIBE_LENGTH) {
      message.error({
        content: t('Short Description should be within {{expr}} characters', {
          expr: MAX_SHORT_DESCRIBE_LENGTH,
        }),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (displayOpening.length > MAX_GREETING_LENGTH) {
      message.error({
        content: t('please keep your input within {{max}} characters', {
          max: MAX_GREETING_LENGTH,
        }),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (plainOpeningForSubmit.length < MIN_GREETING_LENGTH) {
      message.error({
        content: t('please input at least {{min}} characters', {
          min: MIN_GREETING_LENGTH,
        }),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    if (charDialogStyleFirstAllRoleMessage.length > MAX_DIALOG_STYLE_LENGTH) {
      message.error({
        content: t('please keep your input within {{max}} characters', {
          max: MAX_DIALOG_STYLE_LENGTH,
        }),
        icon: <ExclamationCircleOutlined />,
      })
      return false
    }
    return true
  }, [
    charDialogStyleFirstAllRoleMessage.length,
    charGender,
    displayDescription.length,
    displayOpening.length,
    greetingVideoUploading,
    charIsPublic,
    charLongDescribe,
    charNickname,
    plainDescriptionForSubmit,
    plainOpeningForSubmit,
    t,
  ])

  const handleSinglePageSubmit = useCallback(() => {
    if (!validateBeforeSinglePageSubmit()) return
    void handleCreate()
  }, [handleCreate, validateBeforeSinglePageSubmit])

  const singlePageSubmitDisabled =
    (!isUpdateChar && !isCreationConfirmed) ||
    hasProfileLock ||
    greetingVideoUploading ||
    isWorldBookUploading ||
    isPlotFileUploading

  const handleResetForm = useCallback(() => {
    clearLocalDraftCache()
    resetCreationConfirmation()
    resetCharForm()
    resetStoryForm()
    resetStoryUserImageForm()
    setImageCreatorOpen(false)
    setAvatarEditorOpen(false)
    setVoiceModalOpen(false)
    setResetConfirmOpen(false)
    message.success(t('Success'))
  }, [
    clearLocalDraftCache,
    resetCreationConfirmation,
    resetCharForm,
    resetStoryForm,
    resetStoryUserImageForm,
    t,
  ])

  useEffect(() => {
    if (user) {
      getUploadedCodingImg()
        .then((data) => {
          if (!data) return
          setRecords(
            data.image_keys.map((key, index) => ({
              key,
              url: data.image_urls[index],
            }))
          )
        })
        .catch((error) => {
          console.error('Failed to fetch uploaded images:', error)
        })
    }
  }, [user, setRecords])

  const onFirstTcid = useCallback(
    (tcid: string) => {
      trackEvent('TestChat', { TCID: tcid }, 'CLK')
    },
    [trackEvent]
  )

  const handleOpenImageToLink = useCallback(() => {
    setImageToLinkOpen(true)
    setHasViewedImageToLink(true).catch(() => {})
  }, [setHasViewedImageToLink])

  const handleOpenCreationGuide = useCallback(() => {
    setCreationGuideOpen(true)
    trackEvent('CreateGuide_Click', {}, 'CLK')
  }, [trackEvent])

  const handleLoadOfficialExample = useCallback(() => {
    setCharShortDescribe(getOfficialExampleHtml(t))
    trackEvent('LoadOfficialExample_Click', {}, 'CLK')
  }, [setCharShortDescribe, t, trackEvent])

  const handleOpenOpeningGuide = useCallback(() => {
    setOpeningGuideOpen(true)
    trackEvent('OpeningGuide_Click', {}, 'CLK')
  }, [trackEvent])

  const handleLoadOpeningExample = useCallback(() => {
    setCharGreeting(getOfficialExampleOpeningHtml(t))
    trackEvent('LoadOpeningExample_Click', {}, 'CLK')
  }, [setCharGreeting, t, trackEvent])

  const handleOpenLayoutGuide = useCallback(() => {
    setLayoutGuideOpen(true)
    trackEvent('LayoutGuide_Click', {}, 'CLK')
  }, [trackEvent])

  const handleLoadLayoutExample = useCallback(() => {
    setOptimizationCode(getOfficialExampleLayoutHtml(t))
    trackEvent('LoadLayoutExample_Click', {}, 'CLK')
  }, [setOptimizationCode, t, trackEvent])

  const handleOpenOutputGuide = useCallback(() => {
    setOutputGuideOpen(true)
    trackEvent('OutputGuide_Click', {}, 'CLK')
  }, [trackEvent])

  const handleLoadOutputExample = useCallback(() => {
    setOutputFormatConfig(getOfficialExampleOutputText(t))
    trackEvent('LoadOutputExample_Click', {}, 'CLK')
  }, [setOutputFormatConfig, t, trackEvent])

  const handleHtmlStylingChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        if (!styledTagline && charShortDescribe) {
          setStyledTagline(charShortDescribe)
        }
        if (!styledGreeting && charGreeting) {
          setStyledGreeting(charGreeting)
        }
        setContentType(3)
        return
      }

      if (!charShortDescribe || isRichContentLike(charShortDescribe)) {
        setCharShortDescribe(
          richContentToPlainText(styledTagline || charShortDescribe)
        )
      }
      if (!charGreeting || isRichContentLike(charGreeting)) {
        setCharGreeting(richContentToPlainText(styledGreeting || charGreeting))
      }
      setContentType(1)
    },
    [
      charGreeting,
      charShortDescribe,
      setContentType,
      setCharGreeting,
      setCharShortDescribe,
      setStyledGreeting,
      setStyledTagline,
      styledGreeting,
      styledTagline,
    ]
  )

  useEffect(() => {
    if (!isStyledMarkdown) return
    if (charShortDescribe === getOfficialExampleHtml(t)) {
      setCharShortDescribe('')
    }
    if (charGreeting === getOfficialExampleOpeningHtml(t)) {
      setCharGreeting('')
    }
  }, [
    charGreeting,
    charShortDescribe,
    isStyledMarkdown,
    setCharGreeting,
    setCharShortDescribe,
    t,
  ])

  const {
    sendMessage: sendTestMessage,
    resendMessage: resendTestMessage,
    resetHistoryOnly,
    markFirstTipSeen,
    isStreaming: isTestStreaming,
    isWaitingForFirstChunk: isTestWaiting,
    testChat,
  } = useTestChatStream({
    isUpdateChar,
    from: from || 'character_image',
    characterId,
    name: charNickname,
    description: charShortDescribe,
    opening: charGreeting,
    background: charLongDescribe,
    gender: charGender,
    outputFormatConfig: outputFormatConfig || '',
    advancedSetCheck,
    conversationStyle: conversationStyleKey,
    dialogStyle: charForm.charDialogStyle || [],
    validateBeforeSend: validateBeforeTestChatSend,
    onFirstTcid,
  })

  // 获取图片主色调
  useEffect(() => {
    const fetchPrimaryColor = async () => {
      if (charForm.charImagePath) {
        try {
          const result = await getImagePrimaryColor({
            image_key: charForm.charImagePath,
          })
          if (result?.primary_color) {
            setPrimaryColor(result.primary_color)
          }
        } catch (error) {
          console.error('Failed to fetch primary color:', error)
          // 保持默认值
        }
      }
    }
    fetchPrimaryColor()
  }, [charForm.charImagePath])

  // 自动选中指定标签
  useEffect(() => {
    if (!preselectTag || isUpdate) return

    const targetTag = tagOptions.find(
      (tag) =>
        tag.tag === preselectTag || tag.tag === preselectTag.replace(/\s/g, '')
    )

    if (targetTag && !charTagIDs.includes(targetTag.id)) {
      handleClickTag(targetTag.id)
    }
  }, [preselectTag, isUpdate, charTagIDs, handleClickTag, tagOptions])

  const handleDeleteUser = useCallback(() => {
    setUserRoleName('')
    setUserRoleProfile('')
    setUserRoleImageUrl('')
    setUserRoleImage('')
    setUserRoleFaceUrl('')
  }, [
    setUserRoleFaceUrl,
    setUserRoleImage,
    setUserRoleImageUrl,
    setUserRoleName,
    setUserRoleProfile,
  ])

  const removeCharacter = useCallback(
    (id: string) => {
      const newCharacterIds = (role_character_ids || []).filter(
        (item) => item !== id
      )
      setChildCharacterIds(newCharacterIds)
      setRoleCharacterList(
        selectedRoleList.filter((item) => item.character_id !== id)
      )
    },
    [
      role_character_ids,
      selectedRoleList,
      setChildCharacterIds,
      setRoleCharacterList,
    ]
  )

  const removeDrawerCharacter = useCallback((id: string) => {
    setDrawerIdList((prev) => prev.filter((item) => item.character_id !== id))
  }, [])

  const addDrawerCharacter = useCallback(
    (character_id: string, nickname: string, image_url: string) => {
      setDrawerIdList((prev) => {
        if (prev.some((item) => item.character_id === character_id)) {
          return prev
        }
        const maxCount = 20
        if (prev.length >= maxCount) {
          message.error(t('You can only select up to 20 characters'))

          return prev
        }
        return [{ character_id, nickname, image_url }, ...prev]
      })
    },
    [t]
  )

  const applyDrawerSelection = useCallback(() => {
    const nextCharacterIds = drawerIdList.map((item) => item.character_id)
    setChildCharacterIds(nextCharacterIds)
    setRoleCharacterList(drawerIdList)
    setCharacterDrawerOpen(false)
  }, [
    drawerIdList,
    setChildCharacterIds,
    setCharacterDrawerOpen,
    setRoleCharacterList,
  ])

  const hasDraftRoleCharacterSnapshot =
    character_type === 2 &&
    !!charForm.draftLoadedVersion &&
    ((role_character_ids?.length || 0) > 0 ||
      (role_character_list?.length || 0) > 0)

  // 编辑 character_type=2 时，调用 getCharacterPublicByUser 获取子角色列表
  // （剧本/世界书回显在 useCreateDetails 的共享 effect 中处理，单/多角色一致，带三态防覆盖）
  useEffect(() => {
    if (
      !isUpdateChar ||
      character_type !== 2 ||
      !characterId ||
      hasDraftRoleCharacterSnapshot
    ) {
      return
    }
    let cancelled = false
    const fetchCharacterData = async () => {
      try {
        const res = await getCharacterPublicByUser(characterId, true, lang)
        if (cancelled) return
        // role_characters 在响应顶层，包含完整的子角色信息
        if (res.role_characters?.length) {
          const childIds = res.role_characters.map((c) => c.character_id)
          setChildCharacterIds(childIds)
          const builtIdList: IdList[] = res.role_characters.map((c) => ({
            character_id: c.character_id,
            nickname: c.nickname,
            image_url: c.image_url,
          }))
          setRoleCharacterList(builtIdList)
        }
      } catch (error) {
        console.error('Failed to fetch character data for edit:', error)
      }
    }
    fetchCharacterData()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateChar, character_type, characterId, hasDraftRoleCharacterSnapshot])

  const resetDrawerState = useCallback(() => {
    setDrawerIdList(selectedRoleList)
    setSearchText('')
  }, [selectedRoleList])

  const handleSearchChange = (text: string) => {
    setSearchText(text)
  }

  const handleLoadMore = () => {}

  useEffect(() => {
    if (!characterDrawerOpen) return
    resetDrawerState()
  }, [characterDrawerOpen, resetDrawerState])

  // User相关暂时注释掉，后续如果需要再完善
  // const handleUserConfirm = () => {
  //   setUserRoleName(userForm.role_name)
  //   setUserRoleProfile(userForm.role_profile)
  //   setUserRoleImage(userForm.role_pic)
  //   setUserRoleImageUrl(userForm.role_pic_url)
  //   setUserRoleFaceUrl(userForm.role_face_url)
  //   setUserDrawerOpen(false)
  // }

  // const handleUserAvatarApply = () => {
  //   if (!storyUserForm?.charImageUrl || !storyUserForm?.charImagePath) {
  //     message.error(t('Please select image'))
  //     setHasUserAvatar(false)
  //     return
  //   }
  //   setUserForm((prev) => ({
  //     ...prev,
  //     role_pic: storyUserForm.charImagePath || '',
  //     role_pic_url: storyUserForm.charImageUrl || '',
  //     role_face_url:
  //       storyUserForm.charFaceImagePreviewUrl ||
  //       storyUserForm.charImageUrl ||
  //       '',
  //   }))
  //   setUserDrawerTab('profile')
  //   setHasUserAvatar(true)
  // }

  // const handleUserAvatarClear = () => {
  //   setUserForm((prev) => ({
  //     ...prev,
  //     role_pic: '',
  //     role_pic_url: '',
  //     role_face_url: '',
  //   }))
  //   setHasUserAvatar(false)
  // }

  // const isUserNameError = useMemo(() => {
  //   return userForm.role_name.length > 50
  // }, [userForm.role_name.length])

  // const isUserProfileError = useMemo(() => {
  //   return userForm.role_profile.length > 1000
  // }, [userForm.role_profile.length])

  // const userConfirmDisabled = useMemo(() => {
  //   return (
  //     !userForm.role_pic_url ||
  //     !userForm.role_name ||
  //     !userForm.role_profile ||
  //     isUserNameError ||
  //     isUserProfileError
  //   )
  // }, [
  //   isUserNameError,
  //   isUserProfileError,
  //   userForm.role_name,
  //   userForm.role_pic_url,
  //   userForm.role_profile,
  // ])

  // const renderUser = () => {
  //   return (
  //     <div className="flex flex-col items-center">
  //       {user_role_image_url ? (
  //         <div className="relative">
  //           <img
  //             src={user_role_face_url || user_role_image_url}
  //             className="h-[68px] w-[68px] rounded-full object-cover"
  //             alt="User character" //ignore-i18n
  //           />
  //           {!isEdit && (
  //             <button
  //               type="button"
  //               className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#9C4844] opacity-50"
  //               onClick={handleDeleteUser}
  //             >
  //               <CloseOutlinedIcon strokeWidth={2} size={20} />
  //             </button>
  //           )}
  //           <button
  //             type="button"
  //             className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#446D9C] opacity-50"
  //             onClick={openUserDrawer}
  //           >
  //             <ProfileEditCharIcon size={13} />
  //           </button>
  //         </div>
  //       ) : (
  //         <button
  //           type="button"
  //           className="flex h-[68px] w-[68px] items-center justify-center rounded-full border-2 border-dashed border-white/20 bg-white/5"
  //           onClick={openUserDrawer}
  //         >
  //           <UserOutlined
  //             style={{
  //               fontSize: '24px',
  //               color: 'rgba(255,255,255,0.8)',
  //             }}
  //           />
  //         </button>
  //       )}
  //       <span className="mt-2.5 text-sm text-white">{t('User')}</span>
  //     </div>
  //   )
  // }

  const renderCharacter = (item: IdList) => {
    const characterStatus = characterStatusById.get(item.character_id)
    const showProfileLock =
      charIsPublic === 'true' &&
      (characterStatus?.is_public !== true ||
        characterStatus?.review_stage !== ReviewStage.Pass)

    return (
      <div
        className="flex max-w-[68px] shrink-0 flex-col items-center"
        key={item.character_id}
      >
        <div className="relative">
          <img
            src={item.image_url || ''}
            className="h-[68px] w-[68px] rounded-full object-cover"
            alt={item.nickname || ''}
          />
          {showProfileLock && (
            <ProfileLockIcon
              className="absolute left-1/2 top-1/2 z-[99] -translate-x-1/2 -translate-y-1/2"
              size={50}
            />
          )}
          {!isEdit && (
            <button
              type="button"
              className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#000000] opacity-50"
              onClick={() => removeCharacter(item.character_id)}
            >
              <CloseOutlinedIcon strokeWidth={2} size={20} />
            </button>
          )}
        </div>
        <div className="mt-2.5 w-full truncate text-center text-sm text-white">
          {item.nickname}
        </div>
      </div>
    )
  }

  const renderDrawerCharacter = (item: IdList) => {
    return (
      <div
        className="flex max-w-[68px] shrink-0 flex-col items-center"
        key={item.character_id}
      >
        <div className="relative">
          <img
            src={item.image_url || ''}
            className="h-[68px] w-[68px] rounded-full object-cover"
            alt={item.nickname || ''}
          />
          <button
            type="button"
            className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#000000] opacity-50"
            onClick={() => removeDrawerCharacter(item.character_id)}
          >
            <CloseOutlinedIcon strokeWidth={2} size={20} />
          </button>
        </div>
        <div className="mt-2.5 w-full truncate text-center text-sm text-white">
          {item.nickname}
        </div>
      </div>
    )
  }

  const renderDrawerListItem = (
    item: ProfileCharacterList | ISearchResultData,
    isPersonal: boolean
  ) => {
    const isSelected = drawerIdList.some(
      (selected) => selected.character_id === item.character_id
    )
    const isPublic = 'is_public' in item ? item.is_public : undefined
    const reviewStage = 'review_stage' in item ? item.review_stage : undefined
    const isNsfw = 'nsfw' in item ? item.nsfw : false
    return (
      <div
        key={item.character_id}
        className="mb-3 flex items-center justify-between rounded-full bg-white/5 px-3 py-2"
      >
        <div className="flex min-w-0 items-center gap-3">
          <img
            src={item.image_url}
            alt={item.nickname}
            className="h-10 w-10 rounded-full object-cover"
          />
          <div className="flex min-w-0 flex-row items-center justify-center text-sm text-white">
            <div className="truncate">{item.nickname}</div>
            {(typeof isPublic === 'boolean' && !isPublic) ||
            (reviewStage && reviewStage !== ReviewStage.Pass) ? (
              <div className="ml-1.5 flex flex-row items-center justify-center gap-0.5 rounded-full bg-black/20 py-[2px] pl-[3px] pr-[5px]">
                <ProfileLockIcon />
                <span className="text-xs text-white">
                  {typeof isPublic === 'boolean' && !isPublic
                    ? 'Private'
                    : null}
                  {typeof isPublic === 'boolean' &&
                  !isPublic &&
                  reviewStage &&
                  reviewStage !== ReviewStage.Pass
                    ? ' • '
                    : null}
                  {(reviewStage &&
                    reviewStage !== ReviewStage.Pass &&
                    'Pending') ||
                    null}
                </span>
              </div>
            ) : null}
            {isNsfw && (
              <div className="ml-1.5 flex-shrink-0 rounded-full bg-black/20 px-2 py-[2px] text-[10px] text-white">
                18+
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          className={clsx(
            'flex h-9 min-w-[72px] items-center justify-center rounded-full text-xs',
            isSelected
              ? 'cursor-default bg-white/10 text-white/40'
              : 'bg-[#9C4844] text-white'
          )}
          disabled={isSelected}
          onClick={() =>
            addDrawerCharacter(item.character_id, item.nickname, item.image_url)
          }
        >
          {isSelected ? t('Added') : t('Add')}
        </button>
      </div>
    )
  }

  const openImageCreator = (entryType: ImageEntryType) => {
    characterAnalytics.avatarEntryClick()
    setCharForm((prev) => ({
      ...prev,
      createImgStep: entryType === 'generate' ? 'generateImg' : 'uploadImg',
      showUploadGif: entryType === 'gif',
    }))
    setImageCreatorOpen(true)
  }

  const renderImageEntryCard = ({
    type,
    title,
    description,
    icon,
    previewUrl,
  }: {
    type: ImageEntryType
    title: string
    description: string
    icon?: ReactNode
    previewUrl?: string | null
  }) => (
    <button
      type="button"
      className="group relative flex h-[200px] w-[138px] shrink-0 flex-col items-center overflow-hidden rounded-lg border border-dashed border-white/20 bg-white/[0.05] px-2 text-center transition-colors hover:border-white/35 hover:bg-white/[0.08]"
      onClick={() => openImageCreator(type)}
    >
      <div className="mt-[45px] flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md text-white/65">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            className="h-full w-full rounded object-cover"
          />
        ) : icon ? (
          icon
        ) : (
          <span className="text-[32px] font-light leading-none text-white/75">
            +
          </span>
        )}
      </div>
      <div className="mt-[13px] text-sm leading-[14px] text-white/60">
        {title}
      </div>
      <div className="mt-3 max-w-[124px] text-[10px] leading-[14px] text-white/30">
        {description}
      </div>
    </button>
  )

  const renderPillOption = ({
    label,
    selected,
    disabled = false,
    className,
    onClick,
  }: {
    label: ReactNode
    selected: boolean
    disabled?: boolean
    className?: string
    onClick: () => void
  }) => (
    <button
      type="button"
      disabled={disabled}
      className={clsx(
        'flex h-8 min-w-[100px] items-center justify-center rounded-full border px-5 text-[13px] leading-none transition-colors',
        selected
          ? 'border-[#B45A55] bg-[#B45A55] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16)]'
          : 'border-white/10 bg-white/[0.06] text-[#B3B3B3] hover:border-white/25 hover:bg-white/[0.09] hover:text-white',
        disabled
          ? 'cursor-not-allowed opacity-40'
          : 'cursor-pointer hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]',
        className
      )}
      onClick={onClick}
    >
      {label}
    </button>
  )

  return (
    <div
      className="flex w-full flex-row rounded-xl bg-black/10 shadow-[0_2px_4px_0_rgba(255,255,255,0.05)]"
      style={{
        height: 'calc(100vh - 140px)',
      }}
    >
      {contentType === 2 && testChat.isLocked && (
        <div className="fixed inset-0 z-20 bg-black/40" />
      )}
      <div className="relative w-[70%] rounded-es-[10px] rounded-ss-[10px] border-b-[0.5px] border-l-[0.5px] border-t-[0.5px] border-white/20">
        <div
          style={{
            height: `calc(100% - ${!setStep && !isUpdateChar ? 132 : 82}px)`,
          }}
          className={clsx(
            'create-details-scrollbar overflow-y-auto pb-28 pl-5 pr-2 [scrollbar-gutter:stable]'
          )}
        >
          <div className="mt-5">
            <div className="relative text-sm leading-[14px] text-white">
              {t('Create Character Image')}
            </div>
            {charImageUrl ? (
              <div className="mt-2 flex h-[200px] items-start gap-6">
                <button
                  type="button"
                  className="relative h-[200px] w-[138px] shrink-0 overflow-hidden rounded-lg bg-white/[0.05]"
                  onClick={() => openImageCreator('upload')}
                >
                  <Image
                    unoptimized
                    src={transformImage(charImageUrl || '', undefined, 90)}
                    alt={/* i18n-ignore */ 'Character image'}
                    fill
                    className="object-cover"
                  />
                </button>
                <div className="flex w-[132px] flex-col items-center pt-0.5">
                  <button
                    type="button"
                    className="relative flex h-[98px] w-[98px] items-center justify-center overflow-hidden rounded-full bg-white/[0.05]"
                    onClick={() => setAvatarEditorOpen(true)}
                  >
                    {charFaceImagePreviewUrl ? (
                      <img
                        src={charFaceImagePreviewUrl}
                        alt={/* i18n-ignore */ 'Character avatar'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <AvatarEditorItemOutput
                        size={98}
                        image={charImageUrl}
                        croppedArea={charFaceImageCroppedArea}
                        loading={croppingCharFaceImage}
                        className="border-none"
                      />
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-7 flex items-center gap-1 text-sm leading-[14px] text-white/60 transition-colors hover:text-white"
                    onClick={() => setAvatarEditorOpen(true)}
                  >
                    <ProfileEditCharIcon size={14} />
                    <span>{t('Character Avatar')}</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-2 flex gap-4">
                {renderImageEntryCard({
                  type: 'upload',
                  title: t('Upload Image'),
                  description: t('Drag or click to upload'),
                })}
                {renderImageEntryCard({
                  type: 'generate',
                  title: t('Generate Character Image'),
                  description: t('Generate multiple styles with one click'),
                  icon: (
                    <Image
                      src="/create/generate-character-image-icon.png"
                      alt={t('Generate Character Image')}
                      width={48}
                      height={48}
                      className="opacity-80"
                    />
                  ),
                })}
                {renderImageEntryCard({
                  type: 'gif',
                  title: t('Upload Animation'),
                  description: t(
                    "High-resolution GIF or MP4 (9:16) is recommended to animate your character's profile photo"
                  ),
                  previewUrl: uploadGifReferencePreviewUrl || uploadGifUrl,
                })}
              </div>
            )}
            <div className="mt-2 flex max-w-[754px] items-start gap-1 text-xs leading-4 text-white/30">
              <div className="mt-[1px] h-4 w-4 shrink-0">
                <CreateRulesIcon />
              </div>
              <div>
                {t(
                  'Character creation rules: avoid nudity, characters under 18, or unmodified low-quality/unauthorized images. Violations may result in public display restrictions'
                )}
              </div>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-end gap-1.5">
              <div className="relative text-[14px] leading-[14px] text-white">
                {t('Name')} <span className="text-[#ff5454]">*</span>
              </div>
              <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
                <span className="text-white/30">{/* i18n-ignore */}|</span>{' '}
                <span
                  style={{
                    color: isCharNameError
                      ? '#FF5F5F'
                      : charNickname.length === 0
                        ? '#B3B3B3'
                        : '#fff',
                  }}
                >
                  {charNickname.length}
                </span>
                <span
                  style={{
                    color: isCharNameError ? '#FF5F5F' : '#B3B3B3',
                  }}
                >
                  /{MAX_NAME_LENGTH}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-2 h-[42px] rounded bg-white/5">
            <Input
              className={clsx(
                'placeholder:text-gray-756A6E text-sm',
                styles.input
              )}
              style={{
                height: 42,
                resize: 'none',
                background: 'rgb(0 ,0 ,0,0)',
                color: '#FFFFFF',
                border: '0px solid #d9d9d9',
              }}
              variant="borderless"
              maxLength={MAX_NAME_LENGTH}
              placeholder={t('A title or character name')}
              onChange={(e) => setCharNickname(e.target.value)}
              value={charNickname}
            ></Input>
          </div>
          <div className="h-5 text-xs text-[#ff4d4f]">
            {isCharNameError && (
              <span>
                {t('please keep your input within {{max}} characters', {
                  max: MAX_NAME_LENGTH,
                })}
              </span>
            )}
          </div>

          <div className="mb-5">
            <div className="relative text-[15px] text-white">
              {t('Type')} <span className="text-[#ff5454]">*</span>
            </div>
            <div className="mt-2 flex gap-2.5">
              {renderPillOption({
                label: t('Character'),
                selected: character_type === 1,
                disabled: isUpdateChar,
                onClick: () => {
                  if (!isUpdateChar) setCharacterType(1)
                },
              })}
              {renderPillOption({
                label: t('Multi-character'),
                selected: character_type === 2,
                disabled: isUpdateChar || contentType === 2,
                className: 'min-w-[129px]',
                onClick: () => {
                  if (!isUpdateChar && contentType !== 2) {
                    setCharacterType(2)
                    setContentType(1)
                  }
                },
              })}
            </div>
          </div>

          {showFormatField && (
            <div className="mb-5">
              <div className="relative text-[15px] text-white">
                {t('Format')} <span className="text-[#ff5454]">*</span>
              </div>
              <div className="mt-2 flex gap-2.5">
                {renderPillOption({
                  label: /* i18n-ignore */ 'HTML(Beta)',
                  selected: true,
                  disabled: true,
                  onClick: () => undefined,
                })}
              </div>
            </div>
          )}

          <div className="mb-5">
            <div className="relative text-[15px] text-white">
              {t('Main Character Gender')}
              <span className="text-[#ff5454]">*</span>
            </div>
            <div className="mt-2 flex gap-2.5">
              {renderPillOption({
                label: t('Male'),
                selected: charGender === 'male',
                onClick: () => setCharGender('male'),
              })}
              {renderPillOption({
                label: t('Female'),
                selected: charGender === 'female',
                onClick: () => setCharGender('female'),
              })}
              {renderPillOption({
                label: t('Non-binary'),
                selected: charGender === 'other',
                onClick: () => setCharGender('other'),
              })}
            </div>
          </div>

          {showHtmlStylingSwitch && (
            <div className="mb-5">
              <div className="flex items-center gap-2.5">
                <div className="relative text-[14px] leading-[14px] text-white">
                  {t('HTML Styling')}
                </div>
                <Switch
                  className={clsx('!min-h-[19px] !min-w-[36px]', {
                    '!bg-white/20': !isStyledMarkdown,
                    '!bg-[#FF8A00]': isStyledMarkdown,
                  })}
                  size="small"
                  onChange={handleHtmlStylingChange}
                  checked={isStyledMarkdown}
                />
              </div>
              <div className="mt-2 font-regular text-[13px] leading-[18px] text-white/30">
                {t('Rich tags for Description & Opening')}
              </div>
            </div>
          )}

          <div className="flex items-end gap-1.5">
            <div className="relative text-[14px] leading-[14px] text-white">
              {t('Description')} <span className="text-[#ff5454]">*</span>{' '}
            </div>
            <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
              <span className="text-white/30">{/* i18n-ignore */}|</span>{' '}
              <span>{t('Publicly seen')}</span>
            </div>
            <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
              <span className="text-white/30">{/* i18n-ignore */}|</span>{' '}
              <span
                style={{
                  color: isDisplayDescriptionError
                    ? '#FF5F5F'
                    : displayDescription.length === 0
                      ? '#B3B3B3'
                      : '#fff',
                }}
              >
                {Intl.NumberFormat('en-US').format(displayDescription.length)}
              </span>
              <span
                style={{
                  color: isDisplayDescriptionError ? '#FF5F5F' : '#B3B3B3',
                }}
              >
                /{Intl.NumberFormat('en-US').format(MAX_SHORT_DESCRIBE_LENGTH)}
              </span>
            </div>
          </div>
          {isLegacyHtml && (
            <GuideActionBar
              onGuideClick={handleOpenCreationGuide}
              onLoadExample={handleLoadOfficialExample}
            >
              <GuideActionButton
                onClick={handleOpenImageToLink}
                icon="/create/picture-to-link.png"
              >
                {t('Image to link')}
                {!hasViewedImageToLink && (
                  <span className="text-top absolute -right-0.5 -top-3 text-[40px] text-[#ff5454]">
                    ·
                  </span>
                )}
              </GuideActionButton>
            </GuideActionBar>
          )}
          {isStyledMarkdown && (
            <div className="mt-2 flex">
              <GuideActionButton
                onClick={handleOpenImageToLink}
                icon="/create/picture-to-link.png"
              >
                {t('Image to link')}
                {!hasViewedImageToLink && (
                  <span className="text-top absolute -right-0.5 -top-3 text-[40px] text-[#ff5454]">
                    ·
                  </span>
                )}
              </GuideActionButton>
            </div>
          )}
          <div
            key={`description-input-${contentType}`}
            className={clsx(
              'relative mt-2 rounded-lg border pb-11 pr-11 transition-[min-height,background-color,border-color] duration-300 ease-out',
              isDisplayDescriptionError
                ? 'border-[#9c4242] bg-white/5'
                : isStyledMarkdown
                  ? 'border-[rgba(156,72,68,0.40)] bg-[rgba(156,72,68,0.05)]'
                  : 'border-transparent bg-white/5',
              descriptionExpanded ? 'min-h-[320px]' : 'min-h-[60px]'
            )}
          >
            <Input.TextArea
              className={clsx(
                'placeholder:text-gray-756A6E overflow-y-auto text-sm',
                styles.input
              )}
              autoSize={
                descriptionExpanded
                  ? { minRows: 14 }
                  : { minRows: 2, maxRows: 4 }
              }
              variant="borderless"
              style={{
                resize: 'none',
                background: 'rgb(0 ,0 ,0,0)',
                color: '#FFFFFF',
                border: '0px solid #d9d9d9',
                maxHeight: descriptionExpanded ? '400px' : '120px',
              }}
              placeholder={t(
                isStyledMarkdown || isLegacyHtml
                  ? 'Style the description card with HTML. Wrap it in <html-box></html-box> for JavaScript support and better rendering.'
                  : 'Describe your character, story, or both. Shown on the card and at the top of the chat.'
              )}
              onChange={(e) =>
                isRichDisplayContent
                  ? setStyledTagline(e.target.value)
                  : setCharShortDescribe(e.target.value)
              }
              value={displayDescription}
            />
            <button
              type="button"
              className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
              onClick={() => setDescriptionExpanded((v) => !v)}
              aria-label={descriptionExpanded ? t('Collapse') : t('Expand')}
            >
              {descriptionExpanded ? (
                <ChatInspirationCollapseLightIcon size={12} color="#FFFFFF" />
              ) : (
                <ChatInspirationZoomLightIcon size={18} color="#FFFFFF" />
              )}
            </button>
          </div>
          <div className="relative mb-6 h-5 flex-row text-sm text-[#ff4d4f]">
            {isDisplayDescriptionError && (
              <span>
                {t('please keep your input within {{max}} characters', {
                  max: MAX_SHORT_DESCRIBE_LENGTH,
                })}
              </span>
            )}
          </div>

          <div className="flex items-end gap-1.5">
            <div className="text-[14px] leading-[14px] text-white">
              {t('Opening')} <span className="text-[#ff5454]">*</span>
            </div>
            <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
              <span className="text-white/30">{/* i18n-ignore */}|</span>{' '}
              <span
                style={{
                  color: isDisplayOpeningError
                    ? '#FF5F5F'
                    : displayOpening.length === 0
                      ? '#B3B3B3'
                      : '#fff',
                }}
              >
                {Intl.NumberFormat('en-US').format(displayOpening.length)}/
                {Intl.NumberFormat('en-US').format(MAX_GREETING_LENGTH)}
              </span>
            </div>
          </div>
          {isLegacyHtml && (
            <GuideActionBar
              onGuideClick={handleOpenOpeningGuide}
              onLoadExample={handleLoadOpeningExample}
            />
          )}
          <div
            key={`opening-input-${contentType}`}
            className={clsx(
              'relative mt-2 rounded-lg border pb-11 pr-11 transition-[min-height,background-color,border-color] duration-300 ease-out',
              isDisplayOpeningError
                ? 'border-[#9c4242] bg-white/5'
                : isStyledMarkdown
                  ? 'border-[rgba(156,72,68,0.40)] bg-[rgba(156,72,68,0.05)]'
                  : 'border-transparent bg-white/5',
              openingExpanded ? 'min-h-[320px]' : 'min-h-[80px]'
            )}
          >
            <Input.TextArea
              className={clsx(
                'placeholder:text-gray-756A6E overflow-y-auto text-sm',
                styles.input
              )}
              autoSize={
                openingExpanded ? { minRows: 14 } : { minRows: 3, maxRows: 6 }
              }
              variant="borderless"
              style={{
                resize: 'none',
                background: 'rgb(0 ,0 ,0,0)',
                color: '#FFFFFF',
                border: '0px solid #d9d9d9',
                maxHeight: openingExpanded ? '400px' : '180px',
              }}
              placeholder={t(
                isStyledMarkdown || isLegacyHtml
                  ? 'Style your opening with HTML — put dialogue in <message></message>, narration in <narration></narration>, and any visual card in <html-box></html-box> (required for JavaScript).'
                  : 'Write the opening scene. Use "quotes" for dialogue and asterisks for actions/narration. e.g. She looks up. "You\'re early."'
              )}
              onChange={(e) =>
                isRichDisplayContent
                  ? setStyledGreeting(e.target.value)
                  : setCharGreeting(e.target.value)
              }
              value={displayOpening}
            />
            <button
              type="button"
              className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
              onClick={() => setOpeningExpanded((v) => !v)}
              aria-label={openingExpanded ? t('Collapse') : t('Expand')}
            >
              {openingExpanded ? (
                <ChatInspirationCollapseLightIcon size={12} color="#FFFFFF" />
              ) : (
                <ChatInspirationZoomLightIcon size={18} color="#FFFFFF" />
              )}
            </button>
          </div>
          <div className="relative h-5 text-sm text-[#ff4d4f]">
            {displayOpening && displayOpening.length < MIN_GREETING_LENGTH && (
              <span>
                {t('please input at least {{min}} characters', {
                  min: MIN_GREETING_LENGTH,
                })}
              </span>
            )}
            {displayOpening.length > MAX_GREETING_LENGTH && (
              <span>
                {t('please keep your input within {{max}} characters', {
                  max: MAX_GREETING_LENGTH,
                })}
              </span>
            )}
          </div>
          {ENABLE_WEB_GREETING_VIDEO_CONFIG ? (
            <GreetingVideoUploader
              valueKey={greetingVideoKey}
              valueUrl={greetingVideoUrl}
              uploading={greetingVideoUploading}
              setCharForm={setCharForm}
              variant="desktop"
              className="mb-5"
            />
          ) : null}

          <div className="mt-4 flex items-end gap-1.5">
            <div className="relative text-[14px] leading-[14px] text-white">
              {t('Background')}
            </div>
            <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
              <span className="text-white/30">{/* i18n-ignore */}|</span>{' '}
              <span>{t('Public')}</span>
            </div>
            <span className="text-sm text-[#999999]">
              <Switch
                className={clsx('!min-h-[19px] !min-w-[36px]', {
                  '!bg-white/20': !showCharLongDescribe,
                  '!bg-[#FF8A00]': showCharLongDescribe,
                })}
                size="small"
                onChange={(val) => setShowCharLongDescribe(val)}
                checked={showCharLongDescribe}
              />
            </span>
            <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
              <span className="text-white/30">{/* i18n-ignore */}|</span>{' '}
              <span
                style={{
                  color: isCharLongDescribeError
                    ? '#FF5F5F'
                    : charLongDescribe.length === 0
                      ? '#B3B3B3'
                      : '#fff',
                }}
              >
                {Intl.NumberFormat('en-US').format(charLongDescribe.length)}/
                {Intl.NumberFormat('en-US').format(MAX_LONG_DESCRIBE_LENGTH)}
              </span>
            </div>
          </div>
          <div
            className={clsx(
              'relative mt-2 rounded-lg bg-white/5 pb-11 pr-11 transition-[min-height] duration-300 ease-out',
              backgroundExpanded ? 'min-h-[320px]' : 'min-h-[120px]'
            )}
          >
            <Input.TextArea
              className={clsx(
                'placeholder:text-gray-756A6E text-sm',
                styles.input
              )}
              variant="borderless"
              autoSize={
                backgroundExpanded
                  ? { minRows: 14 }
                  : { minRows: 8, maxRows: 12 }
              }
              style={{
                resize: 'none',
                background: 'rgb(0 ,0 ,0,0)',
                color: '#FFFFFF',
                border: isCharLongDescribeError
                  ? '1px solid #9c4242'
                  : '0px solid #d9d9d9',
                maxHeight: backgroundExpanded ? '400px' : '240px',
              }}
              placeholder={t(
                "Define your characters' appearance, personality, and background. You can also add custom prompts, stories, rules, or upload a script/novel. These details will guide how they act and respond"
              )}
              onChange={(e) => setCharLongDescribe(e.target.value)}
              defaultValue={charLongDescribe}
              value={charLongDescribe}
            />
            <button
              type="button"
              className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
              onClick={() => setBackgroundExpanded((v) => !v)}
              aria-label={backgroundExpanded ? t('Collapse') : t('Expand')}
            >
              {backgroundExpanded ? (
                <ChatInspirationCollapseLightIcon size={12} color="#FFFFFF" />
              ) : (
                <ChatInspirationZoomLightIcon size={18} color="#FFFFFF" />
              )}
            </button>
          </div>
          <div className="relative h-5 text-sm text-[#ff4d4f]">
            {isCharLongDescribeError && (
              <span>
                {t('please keep your input within {{max}} characters', {
                  max: MAX_LONG_DESCRIBE_LENGTH,
                })}
              </span>
            )}
          </div>

          {/* HTML 单角色（content_type=2）隐藏整个上传入口：后端对其剧本静默丢弃
              （聊天走 richtext prompt 路径，剧本不注入），世界书入口按产品要求一并收口 */}
          {(character_type === 2 || contentType === 1) && (
            <div className="mt-5" ref={plotUploaderRef}>
              <BackgroundFileUploader
                allowPlot
                plotFileName={plot_file_name || undefined}
                plotFilePrefix={plot_file_prefix || undefined}
                onPlotUploadSuccess={(key, name, prefix) => {
                  setPlotFileKey(key)
                  setPlotFileName(name)
                  setPlotFilePrefix(prefix)
                }}
                onPlotDelete={() => {
                  setPlotFileKey('')
                  setPlotFileName('')
                  setPlotFilePrefix('')
                }}
                onPlotUploadStart={() => setIsPlotFileUploading(true)}
                onPlotUploadEnd={() => setIsPlotFileUploading(false)}
                worldBooks={world_books || []}
                onWorldBookUploadSuccess={(key, name, entryCount) =>
                  addWorldBook(key, name, entryCount)
                }
                onWorldBookDelete={(key) => removeWorldBook(key)}
                onWorldBookUploadStart={() => setIsWorldBookUploading(true)}
                onWorldBookUploadEnd={() => setIsWorldBookUploading(false)}
                onUploaderClick={() => {
                  trackEvent('plot_uploader_click', { platform }, 'CLK')
                }}
                onFileSelected={(fileType, fileSize) => {
                  trackEvent(
                    'plot_file_selected',
                    {
                      file_type: fileType,
                      file_size: fileSize,
                      platform,
                    },
                    'OTHER'
                  )
                }}
                onUploadComplete={(fileType, fileSize) => {
                  trackEvent(
                    'plot_upload_success',
                    {
                      file_type: fileType,
                      file_size: fileSize,
                      platform,
                    },
                    'OTHER'
                  )
                }}
              />
            </div>
          )}

          <div className="mt-5">
            <div className="relative text-[15px] tracking-[0.4px]">
              {t('Rating')} <span className="text-[#ff5454]">*</span>
            </div>
            <div className="mt-2 flex gap-2.5">
              {renderPillOption({
                label: t('Limited'),
                selected: !nsfwCheck,
                disabled: hasNsfwCharacter,
                onClick: () => {
                  if (!hasNsfwCharacter) setNsfwCheck(false)
                },
              })}
              {renderPillOption({
                label: t('Limitless'),
                selected: nsfwCheck,
                onClick: () => setNsfwCheck(true),
              })}
            </div>
            <div className="relative mt-2 w-full text-[13px] text-[#999999]">
              <div className="mr-[3px] inline-block translate-y-[3px]">
                <CreateRulesIcon />
              </div>
              {t(
                "Select rating carefully based on character's image and descriptions. Limitless characters will NOT appear by default unless Limitless mode is enabled to include appropriate 18+ content"
              )}
            </div>
          </div>

          <div className="mt-5">
            <div className="relative text-[15px] tracking-[0.4px]">
              {t('Visibility')} <span className="text-[#ff5454]">*</span>
            </div>
            <div className="mt-2 flex gap-2.5">
              {renderPillOption({
                label: t('Public'),
                selected: charIsPublic === 'true',
                onClick: () => setCharIsPublic('true'),
              })}
              {renderPillOption({
                label: t('Private'),
                selected: charIsPublic === 'false',
                onClick: () => setCharIsPublic('false'),
              })}
            </div>
          </div>

          {character_type === 1 && (
            <div className="mt-5">
              <div className="flex items-center justify-between">
                <div className="relative text-[15px] tracking-[0.4px]">
                  {t('Sound')}
                </div>
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs text-white/30 transition-colors hover:text-white/60"
                  onClick={() => setVoiceModalOpen(true)}
                >
                  {selectedVoice ? t('Edit Voice') : t('Select Sound')}
                  <span aria-hidden>{/* i18n-ignore */}&gt;</span>
                </button>
              </div>
              <button
                type="button"
                className="mt-2 flex h-[42px] w-full items-center justify-between rounded-lg bg-white/5 px-3 text-left text-sm transition-colors hover:bg-white/[0.07]"
                onClick={() => setVoiceModalOpen(true)}
              >
                <span
                  className={clsx(
                    'truncate',
                    selectedVoice ? 'text-white' : 'text-white/30'
                  )}
                >
                  {selectedVoice?.name || t('Select Sound')}
                </span>
                {selectedVoice && (
                  <span className="ml-3 shrink-0 text-xs text-white/30">
                    {t('Speed')} {voiceParam.speed || 1}x
                  </span>
                )}
              </button>
            </div>
          )}

          <div className="mt-5">
            <div className="flex items-end gap-1.5">
              <span className="text-[14px] leading-[14px] text-white">
                {t('Tag')}
              </span>
              <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
                <span className="text-white/30">{/* i18n-ignore */}|</span>{' '}
                <span
                  style={{
                    color: charTagIDs.length === 0 ? '#B3B3B3' : '#fff',
                  }}
                >
                  {charTagIDs.length}
                </span>
                <span>/10</span>
              </div>
            </div>
            <div className="relative mt-3">
              {visibleTagOptions.map((tagItem) => {
                const { id, isNew, isEvent } = tagItem

                return (
                  <button
                    className={clsx(
                      'relative mb-[6px] mr-[6px] inline-flex h-[30px] cursor-pointer select-none items-center gap-1 rounded-full border px-[14px] align-middle text-xs tracking-wide text-white hover:border-white/40',
                      {
                        'border-white/40 bg-white/20': charTagIDs.includes(id),
                        'border-white/5 bg-white/5': !charTagIDs.includes(id),
                      }
                    )}
                    onClick={() => handleClickTag(id)}
                    key={id}
                  >
                    {isNew && (
                      <div className="absolute right-[-8px] top-[-6px]">
                        <TagNewIcon />
                      </div>
                    )}
                    {isEvent && (
                      <div className="absolute right-[-8px] top-[-6px]">
                        <TagEventIcon />
                      </div>
                    )}
                    <TagDisplayLabel tag={tagItem} iconSize={18} />
                  </button>
                )
              })}
            </div>
          </div>

          {character_type === 2 && (
            <>
              <div className="mb-6 mt-6">
                <div className="text-sm text-white">
                  {t('Character List')}（{selectedRoleList.length || 0}）
                </div>
                <div className="mb-4">
                  <span className="text-[13px] text-white/30">
                    {t('Public stories can only include published characters.')}
                  </span>
                  <span className="text-[13px] text-white/30">
                    {t(
                      "Stories containing other users' characters can only be set to private."
                    )}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-[22px]">
                  {selectedRoleList.map((item) => renderCharacter(item))}
                  <div className="flex flex-col items-center">
                    <button
                      type="button"
                      className="flex h-[68px] w-[68px] items-center justify-center rounded-full border-2 border-dashed border-white/20 bg-white/5"
                      onClick={() => setCharacterDrawerOpen(true)}
                    >
                      <PlusOutlined
                        style={{
                          fontSize: '24px',
                          color: 'rgba(255,255,255,0.8)',
                        }}
                      />
                    </button>
                    <div className="mt-2 text-[13px] text-white">
                      {t('Character')}
                    </div>
                  </div>
                  {/* 先暂时删除renderUser */}
                  {/* {renderUser()} */}
                </div>
              </div>
            </>
          )}

          <StoryCharacterDrawer
            open={characterDrawerOpen}
            onClose={() => setCharacterDrawerOpen(false)}
            t={t}
            // userRoleName={user_role_name}
            //renderUser={renderUser}
            drawerIdList={drawerIdList}
            renderDrawerCharacter={renderDrawerCharacter}
            searchText={searchText}
            onSearchChange={handleSearchChange}
            searchResults={searchResults}
            publicCharacterData={publicCharacterData}
            renderDrawerListItem={renderDrawerListItem}
            searchTotal={searchTotal}
            searchLoading={searchLoading}
            onLoadMore={handleLoadMore}
            onConfirm={applyDrawerSelection}
          />
          {/* <StoryUserDrawer
            open={userDrawerOpen}
            onClose={() => setUserDrawerOpen(false)}
            t={t}
            userDrawerTab={userDrawerTab}
            setUserDrawerTab={setUserDrawerTab}
            userForm={userForm}
            setUserForm={setUserForm}
            isUserNameError={isUserNameError}
            isUserProfileError={isUserProfileError}
            userConfirmDisabled={userConfirmDisabled}
            onConfirm={handleUserConfirm}
            onAvatarApply={handleUserAvatarApply}
            onAvatarClear={handleUserAvatarClear}
            hasUserAvatar={hasUserAvatar}
          /> */}
          <div className="mt-5 flex items-center gap-2.5">
            <span className="text-[15px] tracking-[0.4px]">
              {t('More Settings')}
            </span>
            <Switch
              className={clsx('!min-h-[19px] !min-w-[36px]', {
                '!bg-white/20': !advancedSetCheck,
                '!bg-[#FF8A00]': advancedSetCheck,
              })}
              size="small"
              onChange={(val) => setAdvancedSetCheck(val)}
              checked={advancedSetCheck}
            />
          </div>

          {advancedSetCheck && (
            <div className="mt-5">
              {isLegacyHtml && (
                <>
                  <div className="flex items-end gap-1.5">
                    <div className="relative text-[14px] leading-[14px] text-white">
                      {t('Layout Customize')}
                    </div>
                    <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
                      <span className="text-white/30">
                        {/* i18n-ignore */}|
                      </span>{' '}
                      <span
                        style={{
                          color:
                            optimizationCode.length === 0 ? '#B3B3B3' : '#fff',
                        }}
                      >
                        {Intl.NumberFormat('en-US').format(
                          optimizationCode?.length ?? 0
                        )}
                      </span>
                      <span>
                        /
                        {Intl.NumberFormat('en-US').format(
                          MAX_OPTIMIZATION_CODE_LENGTH
                        )}
                      </span>
                    </div>
                  </div>
                  <GuideActionBar
                    onGuideClick={handleOpenLayoutGuide}
                    onLoadExample={handleLoadLayoutExample}
                  >
                    <GuideActionButton
                      onClick={() => setTipsyTagRefOpen((v) => !v)}
                      icon="/create/tag-reference.png"
                      className="ml-auto"
                      iconClassName="opacity-80"
                    >
                      {t('Tipsy Tag Reference')}
                    </GuideActionButton>
                  </GuideActionBar>
                  {tipsyTagRefOpen && (
                    <div className="mt-2 overflow-hidden rounded-lg border border-white/10 bg-white/[0.04]">
                      <div className="bg-white/[0.06] px-4 py-2.5 text-center text-xs font-medium text-white/60">
                        ▼ {t('Tipsy Tag Reference Table')} ▼
                      </div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-white/10 bg-white/[0.04]">
                            <th className="px-4 py-2 text-left font-semibold text-white/70">
                              {t('Tag')}
                            </th>
                            <th className="px-4 py-2 text-left font-semibold text-white/70">
                              {t('Description')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.06]">
                          {[
                            [
                              '#tipsy-tagline-content',
                              t('tipsy_tag_tagline_content'),
                            ],
                            ['#tipsy-message-gap', t('tipsy_tag_message_gap')],
                            [
                              '#tipsy-char-msg-content',
                              t('tipsy_tag_char_msg_content'),
                            ],
                            [
                              '#tipsy-char-message-bubble',
                              t('tipsy_tag_char_message_bubble'),
                            ],
                            [
                              '#tipsy-narration-box',
                              t('tipsy_tag_narration_box'),
                            ],
                            [
                              '#tipsy-user-msg-content',
                              t('tipsy_tag_user_msg_content'),
                            ],
                            [
                              '#tipsy-user-message-bubble',
                              t('tipsy_tag_user_message_bubble'),
                            ],
                            ['#tipsy-chat-header', t('tipsy_tag_chat_header')],
                            [
                              '#tipsy-chat-container',
                              t('tipsy_tag_chat_container'),
                            ],
                            ['#tipsy-chat-bottom', t('tipsy_tag_chat_bottom')],
                            [
                              '#tipsy-role-card-button',
                              t('tipsy_tag_role_card_button'),
                            ],
                            [
                              '#tipsy-chat-input-area',
                              t('tipsy_tag_chat_input_area'),
                            ],
                            [
                              '#tipsy-chat-send-button',
                              t('tipsy_tag_chat_send_button'),
                            ],
                            [
                              '#tipsy-chat-top-gradient',
                              t('tipsy_tag_chat_top_gradient'),
                            ],
                            [
                              '#tipsy-chat-bottom-overlay',
                              t('tipsy_tag_chat_bottom_overlay'),
                            ],
                            [
                              '#tipsy-chat-background-overlay',
                              t('tipsy_tag_chat_background_overlay'),
                            ],
                            [
                              '#tipsy-chat-background-image',
                              t('tipsy_tag_chat_background_image'),
                            ],
                          ].map(([tag, desc]) => (
                            <tr
                              key={tag}
                              className="transition hover:bg-white/[0.04]"
                            >
                              <td className="px-4 py-1.5 font-mono text-white/80">
                                {tag}
                              </td>
                              <td className="px-4 py-1.5 text-white/60">
                                {desc}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div
                    className={clsx(
                      'relative mt-2 rounded-lg bg-white/5 pb-11 pr-11 transition-[min-height] duration-300 ease-out',
                      layoutCustomizeExpanded ? 'min-h-[320px]' : 'min-h-[80px]'
                    )}
                  >
                    <Input.TextArea
                      className={clsx(
                        'placeholder:text-gray-756A6E overflow-y-auto text-sm',
                        styles.input
                      )}
                      autoSize={
                        layoutCustomizeExpanded
                          ? { minRows: 14 }
                          : { minRows: 3, maxRows: 6 }
                      }
                      variant="borderless"
                      style={{
                        resize: 'none',
                        background: 'rgb(0 ,0 ,0,0)',
                        color: '#FFFFFF',
                        border: '0px solid #d9d9d9',
                        maxHeight: layoutCustomizeExpanded ? '400px' : '180px',
                      }}
                      placeholder={t(
                        'Write your global CSS code here to customize your chat layout:\n<style> Insert CSS code here </style>'
                      )}
                      onChange={(e) => setOptimizationCode(e.target.value)}
                      value={optimizationCode}
                      maxLength={MAX_OPTIMIZATION_CODE_LENGTH}
                    />
                    <button
                      type="button"
                      className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
                      onClick={() => setLayoutCustomizeExpanded((v) => !v)}
                      aria-label={
                        layoutCustomizeExpanded ? t('Collapse') : t('Expand')
                      }
                    >
                      {layoutCustomizeExpanded ? (
                        <ChatInspirationCollapseLightIcon
                          size={12}
                          color="#FFFFFF"
                        />
                      ) : (
                        <ChatInspirationZoomLightIcon
                          size={18}
                          color="#FFFFFF"
                        />
                      )}
                    </button>
                  </div>

                  <div className="mt-4 flex items-end gap-1.5">
                    <div className="relative text-[14px] leading-[14px] text-white">
                      {t('Output Setting')}
                    </div>
                    <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
                      <span className="text-white/30">
                        {/* i18n-ignore */}|
                      </span>{' '}
                      <span
                        style={{
                          color:
                            outputFormatConfig.length === 0
                              ? '#B3B3B3'
                              : '#fff',
                        }}
                      >
                        {t('Token:')}{' '}
                        {Intl.NumberFormat('en-US').format(
                          outputFormatConfig.length
                        )}
                      </span>
                    </div>
                  </div>
                  <GuideActionBar
                    onGuideClick={handleOpenOutputGuide}
                    onLoadExample={handleLoadOutputExample}
                  />
                  <div
                    className={clsx(
                      'relative mt-2 rounded-lg bg-white/5 pb-11 pr-11 transition-[min-height] duration-300 ease-out',
                      outputSettingExpanded ? 'min-h-[320px]' : 'min-h-[80px]'
                    )}
                  >
                    <Input.TextArea
                      className={clsx(
                        'placeholder:text-gray-756A6E overflow-y-auto text-sm',
                        styles.input
                      )}
                      autoSize={
                        outputSettingExpanded
                          ? { minRows: 14 }
                          : { minRows: 3, maxRows: 6 }
                      }
                      variant="borderless"
                      style={{
                        resize: 'none',
                        background: 'rgb(0 ,0 ,0,0)',
                        color: '#FFFFFF',
                        border: '0px solid #d9d9d9',
                        maxHeight: outputSettingExpanded ? '400px' : '180px',
                      }}
                      placeholder={t(
                        "Customize the character's output style (HTML/CSS) and narrative settings. Options include narrative pacing, dialogue-to-narration ratio, plot intensity, etc."
                      )}
                      onChange={(e) => setOutputFormatConfig(e.target.value)}
                      value={outputFormatConfig}
                    />
                    <button
                      type="button"
                      className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
                      onClick={() => setOutputSettingExpanded((v) => !v)}
                      aria-label={
                        outputSettingExpanded ? t('Collapse') : t('Expand')
                      }
                    >
                      {outputSettingExpanded ? (
                        <ChatInspirationCollapseLightIcon
                          size={12}
                          color="#FFFFFF"
                        />
                      ) : (
                        <ChatInspirationZoomLightIcon
                          size={18}
                          color="#FFFFFF"
                        />
                      )}
                    </button>
                  </div>
                </>
              )}

              <div className="mt-5">
                <div className="relative text-[15px] tracking-[0.4px]">
                  {t('Conversation Style')}
                </div>
                <div className="mt-2 flex flex-wrap gap-2.5 text-sm">
                  {conversationStyleData?.map((item) => (
                    <Tooltip
                      key={item.id}
                      title={item.intro}
                      overlayInnerStyle={{
                        fontSize: '12px',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        boxShadow: '0px 2px 4px 0px rgba(255,255,255,0.05)',
                        borderRadius: '8px',
                        padding: '0 6px',
                      }}
                    >
                      <span>
                        {renderPillOption({
                          label: (
                            <span className="flex items-center gap-1.5">
                              {item.name}
                              <CreatePromptIcon />
                            </span>
                          ),
                          selected: conversationStyleKey === item.id,
                          className: 'min-w-[116px]',
                          onClick: () => setConversationStyleKey(item.id),
                        })}
                      </span>
                    </Tooltip>
                  ))}
                </div>
              </div>
              {/* 暂时隐藏 Scenario */}
              {/* <div className="relative flex items-center justify-between">
                <div className="text-[15px] tracking-[0.4px]">Scenario</div>
                <div className="text-sm text-[#B3B3B3]">
                  <span
                    style={{
                      color: isScenarioError
                        ? '#FF5F5F'
                        : charScenario.length === 0
                          ? '#B3B3B3'
                          : '#fff',
                    }}
                  >
                    {charScenario.length}
                  </span>
                  <span
                    style={{
                      color: isScenarioError ? '#FF5F5F' : '#B3B3B3',
                    }}
                  >
                    /{Intl.NumberFormat('en-US').format(MAX_SCENARIO_LENGTH)}
                  </span>
                </div>
              </div>
              <div className="mt-2 min-h-[80px] rounded-lg bg-white/5">
                <Input.TextArea
                  className={clsx(
                    'placeholder:text-gray-756A6E overflow-y-hidden text-sm',
                    styles.input
                  )}
                  autoSize
                  variant="borderless"
                  style={{
                    resize: 'none',
                    background: 'rgb(0 ,0 ,0,0)',
                    color: '#FFFFFF',
                    border: isScenarioError
                      ? '1px solid #9c4242'
                      : '0px solid #d9d9d9',
                  }}
                  placeholder="Describe the current situation and context of the conversation"
                  onChange={(e) => setCharScenario(e.target.value)}
                  defaultValue={charScenario}
                  value={charScenario}
                />
              </div>
              <div className="relative h-4 text-sm text-[#ff4d4f]">
                {isScenarioError && (
                  <span>
                    {t('please keep your input within {{max}} characters', {
                      max: MAX_SCENARIO_LENGTH,
                    })}
                  </span>
                )}
              </div> */}

              <div className="mt-4 flex items-end gap-1.5">
                <div className="relative text-[14px] leading-[14px] text-white">
                  {t('Example Dialogues')}
                </div>

                <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
                  <span className="text-white/30">{/* i18n-ignore */}|</span>{' '}
                  <span
                    style={{
                      color: isCharDialogStyleError
                        ? '#FF5F5F'
                        : charDialogStyleFirstAllRoleMessage.length === 0
                          ? '#B3B3B3'
                          : '#fff',
                    }}
                  >
                    {Intl.NumberFormat('en-US').format(
                      charDialogStyleFirstAllRoleMessage?.length ?? 0
                    )}
                  </span>
                  <span
                    style={{
                      color: isCharDialogStyleError ? '#FF5F5F' : '#B3B3B3',
                    }}
                  >
                    /
                    {Intl.NumberFormat('en-US').format(MAX_DIALOG_STYLE_LENGTH)}
                  </span>
                </div>
              </div>
              <div className="mt-2 min-h-20 rounded-lg bg-white/5">
                <Input.TextArea
                  className={clsx(
                    'placeholder:text-gray-756A6E overflow-y-auto text-sm',
                    styles.input
                  )}
                  variant="borderless"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  style={{
                    resize: 'none',
                    background: 'rgb(0 ,0 ,0,0)',
                    color: '#FFFFFF',
                    maxHeight: '180px',
                    border: isCharDialogStyleError
                      ? '1px solid #9c4242'
                      : '0px solid #d9d9d9',
                  }}
                  placeholder={t('Example Dialogue')}
                  onChange={(e) => handleDialogInputChange(e.target.value)}
                  defaultValue={charDialogStyleFirstAllRoleMessage}
                  value={charDialogStyleFirstAllRoleMessage}
                />
              </div>
              <div className="relative h-5 text-sm text-[#ff4d4f]">
                {isCharDialogStyleError && (
                  <span>
                    {t('please keep your input within {{max}} characters', {
                      max: MAX_DIALOG_STYLE_LENGTH,
                    })}
                  </span>
                )}
              </div>
              {canShowReplySetting && (
                <div className="mt-4">
                  <div className="flex items-end gap-1.5">
                    <div className="relative text-[14px] leading-[14px] text-white">
                      {t('Reply Setting')}
                    </div>
                    <div className="relative text-[12px] leading-[12px] text-[#B3B3B3]">
                      <span className="text-white/30">
                        {/* i18n-ignore */}|
                      </span>{' '}
                      <span
                        style={{
                          color: isReplyRequirementError
                            ? '#FF5F5F'
                            : replyRequirement.length === 0
                              ? '#B3B3B3'
                              : '#fff',
                        }}
                      >
                        {Intl.NumberFormat('en-US').format(
                          replyRequirement.length
                        )}
                      </span>
                      <span
                        style={{
                          color: isReplyRequirementError
                            ? '#FF5F5F'
                            : '#B3B3B3',
                        }}
                      >
                        /
                        {Intl.NumberFormat('en-US').format(
                          MAX_REPLY_REQUIREMENT_LENGTH
                        )}
                      </span>
                    </div>
                  </div>
                  <div
                    className={clsx(
                      'relative mt-2 rounded-lg bg-white/5 pb-11 pr-11 transition-[min-height] duration-300 ease-out',
                      replySettingExpanded ? 'min-h-[320px]' : 'min-h-[120px]'
                    )}
                  >
                    <Input.TextArea
                      className={clsx(
                        'placeholder:text-gray-756A6E overflow-y-auto text-sm',
                        styles.input
                      )}
                      autoSize={
                        replySettingExpanded
                          ? { minRows: 14 }
                          : { minRows: 5, maxRows: 8 }
                      }
                      variant="borderless"
                      style={{
                        resize: 'none',
                        background: 'rgb(0 ,0 ,0,0)',
                        color: '#FFFFFF',
                        border: isReplyRequirementError
                          ? '1px solid #9c4242'
                          : '0px solid #d9d9d9',
                        maxHeight: replySettingExpanded ? '400px' : '240px',
                      }}
                      placeholder={t(
                        'Describe how this character should respond, including message length, level of detail, narrative format, tone, and writing style. These instructions will guide how the character speaks, writes, and reacts in chats.'
                      )}
                      onChange={(e) => setReplyRequirement(e.target.value)}
                      value={replyRequirement}
                      maxLength={MAX_REPLY_REQUIREMENT_LENGTH}
                    />
                    <button
                      type="button"
                      className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
                      onClick={() => setReplySettingExpanded((v) => !v)}
                      aria-label={
                        replySettingExpanded ? t('Collapse') : t('Expand')
                      }
                    >
                      {replySettingExpanded ? (
                        <ChatInspirationCollapseLightIcon
                          size={12}
                          color="#FFFFFF"
                        />
                      ) : (
                        <ChatInspirationZoomLightIcon
                          size={18}
                          color="#FFFFFF"
                        />
                      )}
                    </button>
                  </div>
                  {isReplyRequirementError && (
                    <div className="relative h-5 text-sm text-[#ff4d4f]">
                      <span>
                        {t('please keep your input within {{max}} characters', {
                          max: MAX_REPLY_REQUIREMENT_LENGTH,
                        })}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {/* button部分 */}
        {setStep ? (
          <div className="absolute bottom-[20px] left-0 right-0 m-auto flex w-[316px] gap-2.5 pt-5 text-sm">
            <TipsyButton
              className="relative h-10 w-[153px]"
              onClick={goPrevStep}
            >
              {t('Back')}
            </TipsyButton>
            <TipsyButton
              className="relative h-10 w-[153px]"
              onClick={goNextStep}
              variant="filled"
              disabled={nextButtonDisabled || hasProfileLock}
            >
              {hasProfileLock ? t('Contains Private Characters') : t('Next')}
            </TipsyButton>
          </div>
        ) : (
          <div className="absolute bottom-[24px] left-5 flex w-[calc(100%-40px)] flex-col text-[16px]">
            {!isUpdateChar && (
              <CharacterCreationConfirmation
                checked={isCreationConfirmed}
                hasError={showCreationConfirmationError}
                onCheckedChange={setCreationConfirmed}
                className="mb-3"
              />
            )}
            <div className="flex items-center">
              <TipsyButton
                className="relative h-[42px] w-[142px] rounded-[65px] border-white/10 bg-white/5 px-0 text-[16px] font-normal leading-4 text-white hover:bg-white/10"
                onClick={() => setDraftModalOpen(true)}
              >
                <span className="flex items-center justify-center gap-2">
                  <Image
                    src="/create/action-draft-box.png"
                    alt=""
                    width={22}
                    height={22}
                    className="size-[22px] shrink-0 object-contain"
                  />
                  <span>{t('Draft Box')}</span>
                </span>
              </TipsyButton>
              <TipsyButton
                className="relative ml-4 h-[42px] w-[142px] rounded-[65px] border-white/10 bg-white/5 px-0 text-[16px] font-normal leading-4 text-white hover:bg-white/10"
                onClick={() => setResetConfirmOpen(true)}
              >
                <span className="flex items-center justify-center gap-2">
                  <Image
                    src="/create/action-reset.png"
                    alt=""
                    width={22}
                    height={22}
                    className="size-[22px] shrink-0 object-contain"
                  />
                  <span>{t('Reset')}</span>
                </span>
              </TipsyButton>
              <TipsyButton
                className="relative ml-4 h-[42px] w-[142px] rounded-[65px] border-white/10 bg-white/5 px-0 text-[16px] font-normal leading-4 text-white hover:bg-white/10"
                onClick={handleSaveCurrentDraft}
                loading={draftSaving}
                disabled={greetingVideoUploading}
              >
                <span className="flex items-center justify-center gap-2">
                  <Image
                    src="/create/action-save.png"
                    alt=""
                    width={22}
                    height={22}
                    className="size-[22px] shrink-0 object-contain"
                  />
                  <span>{t('Save')}</span>
                </span>
              </TipsyButton>
              <TipsyButton
                className={clsx(
                  'relative ml-6 h-[42px] max-w-[398px] flex-1 rounded-[65px] px-0 text-[16px] font-normal leading-4',
                  singlePageSubmitDisabled
                    ? 'border-white/10 text-white/20'
                    : 'border-transparent text-white hover:border-transparent'
                )}
                variant="filled"
                disabled={singlePageSubmitDisabled}
                loading={createdLoading}
                onClick={handleSinglePageSubmit}
                color={singlePageSubmitDisabled ? undefined : '#9C4844'}
              >
                {hasProfileLock
                  ? t('Contains Private Characters')
                  : isUpdateChar
                    ? t('Update')
                    : t('Create Character')}
              </TipsyButton>
              <Link
                href={
                  contentType === 2
                    ? 'http://cdn.tipsy.chat/docs/html_creator_guide.pdf'
                    : withLocale('/creators-guide', lang)
                }
                target="_blank"
                className="ml-4 flex h-[42px] shrink-0 items-center gap-1 text-[14px] leading-4 text-white/70 transition-colors hover:text-white"
              >
                <CreateIconGuide size={24} />
                <span>{t("Creator's Guide")}</span>
              </Link>
            </div>
          </div>
        )}
      </div>

      <div className="relative z-30 flex h-full w-[30%] min-w-[360px] flex-col overflow-hidden rounded-ee-[10px] rounded-se-[10px] border-[0.5px] border-white/20 px-[19px] pb-[34px]">
        <div className="relative mb-2 mt-5 h-7 shrink-0 text-[15px] tracking-[0.4px] text-[#B3B3B3]">
          <span className="absolute left-1/2 top-1/2 h-6 -translate-x-1/2 -translate-y-1/2">
            {t('Preview')}
          </span>
          {contentType === 2 && testChat.isLocked && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2">
              <TestChatLocalTooltip
                visible={testChat.showFirstTip}
                onClose={markFirstTipSeen}
                outsideClosable={false}
                content={
                  <div className="flex items-start gap-2">
                    <span className="flex h-8 w-8 shrink-0 items-start justify-center text-[32px] leading-none">
                      🎉
                    </span>
                    <p className="text-[12px] font-normal leading-4 text-white/80">
                      {t(
                        "You're in Test Chat mode. Editing is locked during testing. Click Reset to edit your character."
                      )}
                    </p>
                  </div>
                }
                width={290}
                alignRightEdgeToTrigger={true}
                horizontalOffset={20}
                triggerGap={10}
                closeIcon={<TestChatTipCloseIcon width={24} height={24} />}
              >
                <button
                  type="button"
                  className="flex h-6 items-center gap-0.5 rounded-[36px] border-[0.5px] border-white/50 bg-white/5 px-2 text-[14px] leading-none text-[#B3B3B3]"
                  onClick={resetHistoryOnly}
                >
                  <TestChatRefreshIcon
                    width={20}
                    height={20}
                    color={'#B3B3B3'}
                  />
                  {t('Reset')}
                </button>
              </TestChatLocalTooltip>
            </div>
          )}
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center">
          {isLegacyHtml || isStyledMarkdown ? (
            // Legacy HTML / Styled Markdown 模式：使用完整 HTML Preview（试聊仅 Legacy HTML 开放）
            <div
              className="relative h-full max-w-full overflow-hidden rounded-lg"
              style={{ aspectRatio: UPLOAD_IMG_CROP_RATIO }}
            >
              <style
                dangerouslySetInnerHTML={{
                  __html: `
                  .preview-container-desktop .background-image,
                  .preview-container-desktop .background-overlay {
                    position: absolute !important;
                    width: 100% !important;
                    height: 100% !important;
                    top: 0 !important;
                    left: 0 !important;
                  }
                  .preview-container-desktop .chat-container {
                    height: 100% !important;
                    position: relative !important;
                  }
                `,
                }}
              />
              <div
                className="preview-container-desktop"
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  isolation: 'isolate',
                }}
              >
                <PreviewDom
                  characterName={charNickname || ''}
                  characterFaceUrl={charFaceImagePreviewUrl || ''}
                  characterImageUrl={charImageUrl || ''}
                  userName={user?.nickname || ''}
                  tagline={displayDescription || ''}
                  greeting={displayOpening || ''}
                  globalCss={isLegacyHtml ? optimizationCode || '' : ''}
                  primaryColor={primaryColor}
                  safeAreaTop={0}
                  safeAreaBottom={0}
                  onBack={() => {}}
                  isStyledMarkdown={isStyledMarkdown}
                  enableTestChat={isLegacyHtml}
                  modelLabel={t('Preview')}
                  roleCardName={user?.nickname || 'You'}
                  isMutating={isTestStreaming || isTestWaiting}
                  testChatHistory={testChat.history}
                  testChatPending={testChat.pending}
                  testChatStreamingContent={testChat.streamingContent}
                  isWaitingForFirstChunk={isTestWaiting}
                  onSendMessage={sendTestMessage}
                  onResendMessage={resendTestMessage}
                  enterToSend={true}
                />
              </div>
            </div>
          ) : (
            // Markdown 模式：保持原有预览逻辑
            <div
              className="relative h-full max-w-full overflow-hidden rounded-lg"
              style={{ aspectRatio: UPLOAD_IMG_CROP_RATIO }}
            >
              {charImageUrl && (
                <Image
                  unoptimized
                  src={transformImage(charImageUrl || '', undefined, 90)}
                  alt={/* i18n-ignore */ 'Character'}
                  className="object-contain"
                  fill={true}
                />
              )}
              <div
                className="absolute bottom-0 left-0 flex h-1/2 w-full flex-col justify-end px-[21px] pb-[21px]"
                style={{
                  background:
                    'linear-gradient( 180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 100%)',
                }}
              >
                <div className="flex items-center">
                  <div className="h-[78px] w-[78px] rounded-[18px] border border-solid border-white/80">
                    <Avatar
                      src={charFaceImagePreviewUrl}
                      alt={/* i18n-ignore */ 'Character avatar'}
                      size={64}
                      style={{
                        margin: 6,
                        border: '1px solid rgba(255,255,255,0.5)',
                      }}
                    />
                  </div>
                  <div className="ml-2 overflow-hidden">
                    <div className="truncate text-xl">{charNickname}</div>
                    <div>
                      {selectedTagItems.map((tagItem) => (
                        <Tag
                          style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            color: 'white',
                            border: '0.5px solid rgba(255, 255, 255, 0.40)',
                          }}
                          key={tagItem.id}
                        >
                          <TagDisplayLabel tag={tagItem} iconSize={14} />
                        </Tag>
                      ))}
                    </div>
                  </div>
                </div>
                {charShortDescribe && (
                  <div className="line-clamp-6 pt-[8px] text-xs">
                    {charShortDescribe}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <CreationGuideModal
        open={creationGuideOpen}
        onClose={() => setCreationGuideOpen(false)}
      />
      <OpeningGuideModal
        open={openingGuideOpen}
        onClose={() => setOpeningGuideOpen(false)}
      />
      <LayoutGuideModal
        open={layoutGuideOpen}
        onClose={() => setLayoutGuideOpen(false)}
      />
      <OutputGuideModal
        open={outputGuideOpen}
        onClose={() => setOutputGuideOpen(false)}
      />
      <ImageToLinkModal
        open={imageToLinkOpen}
        onClose={() => setImageToLinkOpen(false)}
        scene={'user_coding'}
        records={records}
        setRecords={setRecords}
      />
      <Modal
        open={resetConfirmOpen}
        centered
        maskClosable={false}
        footer={null}
        width={368}
        closeIcon={false}
        onCancel={() => setResetConfirmOpen(false)}
        styles={{
          content: {
            padding: 0,
            overflow: 'hidden',
            borderRadius: 16,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(11, 2, 15, 0.88)',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(14px)',
          },
          body: {
            background: 'transparent',
          },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.42)',
          },
        }}
      >
        <div className="px-6 py-5 text-center text-white">
          <div className="text-lg font-medium">{t('Reset')}</div>
          <div className="mt-4 text-sm leading-5 text-white/60">
            {t(
              'Reset will clear the current form. Unsaved changes will be lost.'
            )}
          </div>
          <div className="mt-6 flex gap-3">
            <TipsyButton
              className="h-[42px] flex-1 rounded-[65px] border-white/10 bg-white/5 text-[16px] font-normal text-white hover:bg-white/10"
              onClick={() => setResetConfirmOpen(false)}
            >
              {t('Cancel')}
            </TipsyButton>
            <TipsyButton
              className="h-[42px] flex-1 rounded-[65px] border-transparent text-[16px] font-normal text-white hover:border-transparent"
              variant="filled"
              color="#9C4844"
              onClick={handleResetForm}
            >
              {t('Confirm')}
            </TipsyButton>
          </div>
        </div>
      </Modal>
      <CharacterDraftModal
        open={draftModalOpen}
        total={draftTotal}
        loading={draftLoading}
        items={draftItems}
        deletingDraftId={draftDeletingId}
        onClose={() => setDraftModalOpen(false)}
        onLoad={handleLoadDraftById}
        onDelete={deleteDraftById}
        onExposure={characterAnalytics.draftBoxExposure}
      />
      <RestoreLocalDraftModal
        open={restoreModalOpen}
        onRestore={handleRestoreCache}
        onDiscard={discardCache}
      />
      {charImageUrl && (
        <AvatarEditorItem
          image={charImageUrl}
          open={avatarEditorOpen}
          initialCroppedAreaPixels={charFaceImageCroppedAreaPixels}
          title={t('Edit Avatar')}
          onOk={handleSaveFaceImg}
          onCancel={() => setAvatarEditorOpen(false)}
        />
      )}
      <Modal
        open={voiceModalOpen}
        centered
        destroyOnClose={false}
        footer={null}
        width="min(835px, calc(100vw - 80px))"
        closeIcon={<CloseOutlinedIcon strokeWidth={2} size={20} />}
        onCancel={() => setVoiceModalOpen(false)}
        styles={{
          content: {
            padding: 0,
            overflow: 'hidden',
            borderRadius: 16,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(11, 2, 15, 0.88)',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(14.35px)',
          },
          body: {
            background: 'transparent',
          },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(2px)',
          },
        }}
      >
        <div className="flex h-[min(610px,calc(100vh-96px))] flex-col px-6 py-4 text-white">
          <div className="flex h-8 shrink-0 items-center justify-between">
            <div className="text-sm leading-[14px]">{t('Sound')}</div>
          </div>
          <div
            className={clsx(
              'mt-5 shrink-0 rounded-lg border border-dashed border-white/20 bg-white/5',
              selectedVoice
                ? 'min-h-[128px] px-4 py-3'
                : 'flex h-[56px] items-center px-[15px]'
            )}
          >
            <AudioProvider>
              {selectedVoice ? (
                <SelectedVoiceItem
                  voice={selectedVoice}
                  onRemove={handleRemoveVoice}
                  onSpeedChange={handleSpeedChange}
                  speed={voiceParam.speed || 1}
                  compact
                />
              ) : (
                <div className="w-full text-center text-[10px] leading-[14px] text-white/30">
                  {t('Choose a voice that brings your character to life.')}
                </div>
              )}
            </AudioProvider>
          </div>
          <div className="mt-4 flex h-9 shrink-0 items-center rounded-full bg-white/5 px-3">
            <SearchIcon color="#B3B3B3" size={20} opacity={0.5} />
            <input
              type="text"
              value={voiceSearchInput}
              onChange={(event) =>
                handleVoiceSearchInputChange(event.target.value)
              }
              placeholder={t('Search by voice name or traits')}
              className="ml-2 min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/30"
            />
            {voiceSearchInput && (
              <button
                type="button"
                className="flex h-5 w-5 shrink-0 items-center justify-center text-white/50 transition-colors hover:text-white"
                onClick={handleVoiceSearchClear}
                aria-label={t('Clear')}
              >
                <CloseOutlinedIcon size={18} strokeWidth={1.5} />
              </button>
            )}
          </div>
          <div className="mt-3 flex shrink-0 items-center gap-9">
            {voiceFilterOptions.map((option) => (
              <button
                type="button"
                key={option.value}
                className={clsx(
                  'relative h-6 text-sm leading-[14px] transition-colors',
                  selectedVoiceFilter === option.value
                    ? 'text-white'
                    : 'text-white/30 hover:text-white/60'
                )}
                onClick={() => handleVoiceFilterChange(option.value)}
              >
                {option.label}
                {selectedVoiceFilter === option.value && (
                  <span className="absolute bottom-0 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-[#9C4844]" />
                )}
              </button>
            ))}
          </div>
          <div className="mt-2 flex min-h-0 flex-1 overflow-hidden">
            <VoiceList
              voices={voices}
              selectedVoice={selectedVoice}
              onSelectVoice={handleSelectVoice}
              isLoading={isVoiceLoading}
              isLoadingMore={isVoiceLoadingMore}
              onLoadMore={loadMoreVoices}
              hasMore={hasMoreVoices}
              variant="creatorModal"
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={imageCreatorOpen}
        centered
        destroyOnClose={false}
        footer={null}
        width="min(1180px, calc(100vw - 80px))"
        closeIcon={<CloseOutlinedIcon strokeWidth={2} size={20} />}
        onCancel={() => setImageCreatorOpen(false)}
        styles={{
          content: {
            padding: 0,
            overflow: 'hidden',
            borderRadius: 16,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(11, 2, 15, 0.88)',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(14px)',
          },
          body: {
            background: 'transparent',
          },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.46)',
            backdropFilter: 'blur(2px)',
          },
        }}
      >
        <CreateImg
          scene={from || 'character_image'}
          hideNext
          onConfirm={() => setImageCreatorOpen(false)}
          modalSurface
          containerHeight="min(760px, calc(100vh - 96px))"
        />
      </Modal>
    </div>
  )
}
